/* ============================================================
   BrandKitScreen — the Brand Kit library at /app/brandkit, third
   surface of the Marketing hub. Identity strip (primary logo +
   brand name/colors) + tagged asset grid + upload + per-asset
   detail (retag / set-as-primary-logo / delete).

   Data: asset list lives in LOCAL state (the global dashboard
   provider only auto-refreshes on the dashboard route). list() is
   cacheTTL:0 so it's always live; every mutation re-fetches —
   cache invalidation alone clears cache but does not repaint.
   Bytes are served as-is from the public /media URL (asset.url),
   capped at 5 MB on upload. Gated by brandKitEnabled().
   ============================================================ */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icons as I, Svg } from '../components/pack-icons';
import MarketingSubnav from '../components/MarketingSubnav.jsx';
import { api, deleteBrandKitAsset } from '../services/api/client.js';
import { brandKitEnabled, brandTypographyEnabled, videoReelsEnabled, videoReelsGA, videoCinematicEnabled } from '../lib/flags.js';
import { videoQuotaInfo } from '../lib/video-quota.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useUpgrade } from '../contexts/UpgradeContext.jsx';
import { isSvgFile, rasterizeSvgToPng } from '../lib/rasterize.js';
import { toBrandForm } from './content-plan/shared/brandFormUtils.js';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { useGenerationStatus, OPERATION_KINDS } from '../state/GenerationStatusContext.jsx';
import { formatDuration } from '../components/preview/normalizePost.js';
import VideoFramePreview from '../components/VideoFramePreview.jsx';
import { VIDEO_ACCEPT, VIDEO_ACCEPT_SET, MAX_VIDEO_BYTES, MAX_VIDEO_SECONDS, readVideoMetaAndPoster } from '../lib/video-upload.js';
import { deriveBrandKitMediaLock } from '../lib/brandKitMediaLock.js';
import { freePublishingState } from '../lib/freePublishing.js';
import { effectiveAccessState } from '../lib/billingState.js';
import CreationAccessNotice, { creationAccessState } from '../components/billing/CreationAccessNotice.jsx';
import { storageMeterState } from '../lib/storageMeter.js';
import { useDismissableMenu } from '../hooks/useDismissableMenu.js';
import { PRIMARY_LOGO_UPDATED_EVENT } from '../lib/brandLogo.js';
// Reuse New Post's reference picker VERBATIM (the same window, size, tag filters
// & behaviour) so the Media generator's picker is identical, not a lookalike.
import { LibraryPicker } from './CreatePost.jsx';
import { useUploadProgress } from '../components/UploadProgress.jsx';
import FontCatalogModal from '../components/brand-kit/FontCatalogModal.jsx';
import { isPreviewBlocked, wallSourceForReason } from '../lib/freePreview.js';
import {
  collectTypographyLanguages,
  languagesNeedCyrillic,
  loadGoogleFont,
  renderTypographySpecimen,
  specimenGlyphs,
} from '../lib/brand-typography.js';

const TAGS = ['logo', 'product', 'people', 'transport', 'style'];
const TAG_SET = new Set(TAGS);
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const ACCEPT_SET = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
// SVG is allowed into the PICKER only — we rasterize it to PNG client-side
// before upload (the server still rejects raw SVG as a stored-XSS vector).
const PICK_ACCEPT = `${ACCEPT},image/svg+xml`;

// Presentation only: post/video/font filters are not editable asset tags.
function TagIcon({ tag }) {
  const props = { size: 14, 'aria-hidden': true, focusable: false };
  let icon;
  switch (tag) {
    case 'all': icon = <I.grid {...props} />; break;
    case 'logo': icon = <Svg {...props}><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9z" /></Svg>; break;
    case 'product': icon = <Svg {...props}><path d="m12 3 9 5-9 5-9-5zM3 8v9l9 5 9-5V8M12 13v9M7.5 5.5l9 5" /></Svg>; break;
    case 'people': icon = <I.users {...props} />; break;
    case 'transport': icon = <Svg {...props}><path d="M3 16V6h11v10M14 10h4l3 4v4h-3M8 18h6M3 16v2h1" /><circle cx="6" cy="18" r="2" /><circle cx="16" cy="18" r="2" /></Svg>; break;
    case 'style': icon = <I.sparkles {...props} />; break;
    case 'posts':
    case 'post': icon = <I.layers {...props} />; break;
    case 'videos': icon = <Svg {...props}><rect x="3" y="6" width="13" height="12" rx="2" /><path d="m16 10 5-3v10l-5-3" /></Svg>; break;
    case 'font': icon = <Svg {...props}><path d="M4 8V5h16v3M12 5v14M8 19h8" /></Svg>; break;
    default: icon = <I.image {...props} />;
  }
  return <span className={`bk-tag-icon bk-tag-icon-${tag}`} aria-hidden="true">{icon}</span>;
}

function MediaUploadIcon({ video = false }) {
  return (
    <Svg size={22} aria-hidden="true" focusable="false">
      {video ? <><rect x="3" y="5" width="13" height="14" rx="2" /><path d="m16 10 5-3v10l-5-3M9.5 15V9M7 11.5 9.5 9l2.5 2.5" /></>
        : <><path d="M14 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-9M16 5h6M19 2v6" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m3 17 5-5 4 4 3-3 6 6" /></>}
    </Svg>
  );
}

// Art-direction controls → prompt fragments. Mirrors the New Post image card's
// stylePrefix/brandCtx stitching: every control is just one more clause folded
// into the free-form prompt client-side, so NO backend change is needed. Empty
// string = no fragment (the description alone carries it).
const STYLE_FRAG = {
  photo: '',
  illustration: 'Illustration style.',
  '3d': '3D rendered style.',
  minimal: 'Minimalist, clean composition with negative space.',
  bold: 'Bold, high-contrast, vibrant.',
  vintage: 'Vintage, retro aesthetic.',
  watercolour: 'Watercolour painting style.',
  lineart: 'Clean line-art illustration.',
};
// On-brand keeps it light (4); brand-free reveals the expanded set (8).
// One Style set for BOTH on-brand and brand-free — the choices are the same
// look-and-feel regardless of whether the brand palette is applied.
const STYLE_CUSTOM = ['photo', 'illustration', '3d', 'minimal', 'bold', 'vintage', 'watercolour', 'lineart'];
const MOOD_FRAG = { clean: 'clean, uncluttered mood', warm: 'warm, inviting mood', moody: 'moody, atmospheric mood', energetic: 'energetic, dynamic mood', calm: 'calm, serene mood', premium: 'premium, high-end feel' };
const LIGHT_FRAG = { natural: 'natural lighting', golden: 'golden-hour lighting', studio: 'studio lighting', soft: 'soft, diffused lighting', dramatic: 'dramatic lighting', neon: 'neon lighting' };
const COMP_FRAG = { closeup: 'close-up composition', wide: 'wide-shot composition', flatlay: 'flat-lay composition', centered: 'centered composition', thirds: 'rule-of-thirds composition', negspace: 'composition with generous negative space' };
const MOOD_KEYS = Object.keys(MOOD_FRAG);
const LIGHT_KEYS = Object.keys(LIGHT_FRAG);
const COMP_KEYS = Object.keys(COMP_FRAG);
// Max reference images a generation may condition on (matches the visual-worker
// composition cap + the backend MAX_GEN_REFERENCES).
const MAX_GEN_REFS = 3;
// Max scenes (image frames) a video may stitch — a SEPARATE, higher cap than
// MAX_GEN_REFS (the image-reference cap); the backend slices at 8 regardless.
// Do NOT reuse MAX_GEN_REFS here (video-forms-v2-refresh-design-brief.md §12.2).
const MAX_REEL_FRAMES = 8;

// gpt-image honours human colour NAMES far better than raw hex, so the custom
// palette is described as "name (#hex)" — the name steers, the hex is a hint.
// Nearest-anchor match by squared RGB distance over a curated named set.
const NAMED_COLOURS = [
  ['black', '#000000'], ['white', '#ffffff'], ['grey', '#808080'], ['light grey', '#cccccc'], ['dark grey', '#404040'],
  ['red', '#e53935'], ['dark red', '#8e1b1b'], ['crimson', '#b3003c'], ['maroon', '#5d1a1a'],
  ['orange', '#fb8c00'], ['amber', '#ffb300'], ['gold', '#d4a53f'], ['yellow', '#fdd835'],
  ['cream', '#f3e9d2'], ['beige', '#e8d8b8'], ['tan', '#c19a6b'], ['brown', '#795548'], ['dark brown', '#3b2417'],
  ['olive', '#808000'], ['lime', '#9ccc65'], ['green', '#43a047'], ['dark green', '#1b5e20'],
  ['teal', '#00897b'], ['mint', '#a7e8d0'], ['cyan', '#00bcd4'], ['sky blue', '#4fc3f7'],
  ['blue', '#1e88e5'], ['navy', '#1a237e'], ['indigo', '#3949ab'],
  ['purple', '#8e24aa'], ['violet', '#b39ddb'], ['magenta', '#d81b60'], ['pink', '#ec90b8'], ['rose', '#f48fb1'],
];
function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function describeColor(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  let best = null, bestD = Infinity;
  for (const [name, anchor] of NAMED_COLOURS) {
    const a = hexToRgb(anchor);
    const d = (rgb[0] - a[0]) ** 2 + (rgb[1] - a[1]) ** 2 + (rgb[2] - a[2]) ** 2;
    if (d < bestD) { bestD = d; best = name; }
  }
  return best;
}

// Assets created via onboarding carry types outside the 5 Brand Kit tags
// (style_reference, brand_screenshot, previous_post, website_screenshot, …).
// Map them onto a canonical tag for DISPLAY only — the stored value is never
// changed — so chips / filters / counts never render a raw i18n key like
// "brandkit.tag_style_reference".
function normalizeTag(raw) {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (TAG_SET.has(v)) return v;
  if (v === 'product_photo') return 'product';
  return 'style';
}

function unwrap(res) {
  return res?.data ?? res;
}

function Star({ size = 13, filled = true }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={filled ? 0 : 2} aria-hidden="true">
      <path d="M12 2 15 9l7 .5-5.3 4.6L18.5 21 12 17.3 5.5 21l1.8-6.9L2 9.5 9 9z" />
    </svg>
  );
}

// §6 B3 — client-side elapsed timer for the rendering tile. Ticks mm:ss off
// the op's startedAt (Date.now() epoch, already exposed by GenerationStatusContext);
// falls back to the static ETA-only string when no timestamp is available (§12.5).
function ElapsedEta({ startedAt }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  const eta = t('media.videoEta', { defaultValue: 'usually 2–5 minutes' });
  if (!startedAt) return <span className="dv-render-eta">{eta}</span>;
  const sec = Math.max(0, Math.floor((now - startedAt) / 1000));
  const m = Math.floor(sec / 60);
  const s = String(sec % 60).padStart(2, '0');
  return <span className="dv-render-eta">{eta} · {m}:{s}</span>;
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });
}


export default function BrandKitScreen() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { openUpgrade } = useUpgrade();

  const [assets, setAssets] = useState([]);
  const [primaryLogoId, setPrimaryLogoId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState('all');
  const [brandProfile, setBrandProfile] = useState(null);
  const [typography, setTypography] = useState(null);
  const [fontCatalogOpen, setFontCatalogOpen] = useState(false);
  const { track: trackUpload } = useUploadProgress();
  const [fontMediaPickerOpen, setFontMediaPickerOpen] = useState(false);
  const [fontRendering, setFontRendering] = useState(false);
  const [fontError, setFontError] = useState(null);
  const [assetMenuId, setAssetMenuId] = useState(null);
  // TASK-326: the per-asset "..." popover otherwise stays open until one of
  // its own action buttons is clicked. assetMenuRef tracks the currently
  // rendered popover element (only one can be open at a time — assetMenuId
  // is a single value); assetMenuTriggerRef is stashed by the kebab
  // button's own onClick (below) so useDismissableMenu can exempt it from
  // the outside-pointerdown check and return focus to it on close.
  const assetMenuRef = useRef(null);
  const assetMenuTriggerRef = useRef(null);
  // Fix list item 13: the hook's returned `close` (not a bare setter) is
  // what every dismissal path uses now — outside pointerdown, Escape, AND
  // each menu item's own onClick — so choosing an action returns focus to
  // the kebab exactly like any other dismissal does.
  // N6 (judge re-verify): a stable onClose — an inline arrow re-armed the
  // hook's capture listeners on every render while the menu was open.
  const onCloseAssetMenu = useCallback(() => setAssetMenuId(null), []);
  const closeAssetMenu = useDismissableMenu(assetMenuId != null, onCloseAssetMenu, { menuRef: assetMenuRef, triggerRef: assetMenuTriggerRef });
  const [uploading, setUploading] = useState(false);
  // "Upload your own video" — separate busy flag from the image `uploading`
  // state above (distinct affordance, distinct button, can't collide).
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: 'error'|'info', text }
  const [detail, setDetail] = useState(null); // asset open in modal
  const [busyId, setBusyId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [genPrompt, setGenPrompt] = useState('');
  const [genStyle, setGenStyle] = useState('photo'); // one of STYLE_FRAG keys
  const [genAspect, setGenAspect] = useState('square'); // square | portrait | landscape
  const [genBrandAware, setGenBrandAware] = useState(true);
  // Brand-free art-direction (only steer the prompt when the gateway is OFF).
  // Each is single-select & optional (blank → inferred from the description).
  const [genMood, setGenMood] = useState('');
  const [genLighting, setGenLighting] = useState('');
  const [genComposition, setGenComposition] = useState('');
  const [genPalette, setGenPalette] = useState([]); // custom hex list (distinct from brand palette)
  const [pickedRefs, setPickedRefs] = useState([]); // [{id,url,tag}] reference images the gen conditions on (max MAX_GEN_REFS)
  const [refPickerOpen, setRefPickerOpen] = useState(false);
  // Describe-mode reference photos ("1 click with a reference") — a SEPARATE
  // picked-set from pickedRefs (which photos-mode already owns as its required
  // scene source, and image-mode owns as its style references) so describe mode
  // doesn't fight either for the same state. Optional; capped at MAX_GEN_REFS
  // (conditioning pixels, not scene content — same cap as image-mode refs).
  const [describeRefs, setDescribeRefs] = useState([]); // [{id,url,tag}]
  const [describeRefPickerOpen, setDescribeRefPickerOpen] = useState(false);
  const [genAdvancedOpen, setGenAdvancedOpen] = useState(false); // Mood/Lighting/Composition/Palette hidden behind "More options"
  const [genOpen, setGenOpen] = useState(false); // the describe/generate panel is collapsed by default
  const [confirmDel, setConfirmDel] = useState(null); // asset pending styled delete-confirm
  // Video (9:16 narrated slideshow / describe→video) generation — DARK admin
  // pilot. genKind flips the generator between Image and Video; the Video entry
  // is hidden unless BOTH the SPA flag and admin (the server double-gates
  // identically). Video mode has two mutually-exclusive input sources
  // (lite-first addendum §2): 'describe' (AI plans 4 scenes from a text prompt)
  // and 'photos' (v1 reel — stitches the picked frames, pickedRefs).
  const [genKind, setGenKind] = useState('image'); // 'image' | 'video'
  const [srcMode, setSrcMode] = useState('describe'); // 'describe' | 'photos' — video-kind only, default describe
  const [reelNarration, setReelNarration] = useState(true);
  // Tier-2 cinematic quality (admin pilot): 'standard' (json2video slideshow) |
  // 'cinematic' (fal.ai/Kling). Only sent in describe mode; server double-gates.
  const [reelQuality, setReelQuality] = useState('standard');
  // Set once the user picks a quality by hand, so the "default to cinematic when
  // available" effect never overrides an explicit choice.
  const reelQualityTouchedRef = useRef(false);
  // Optional hook-line override (first 3 seconds, ≤9 words). Empty = the AI
  // writes the hook (buildReelContract); non-empty = used verbatim on-screen.
  const [reelHook, setReelHook] = useState('');
  // Narration script — a first-class field in Video mode (the .dv-script
  // textarea), separate from genPrompt (which becomes the describe-mode prompt).
  const [reelScript, setReelScript] = useState('');
  // Per-video narration language override. '' = follow the brand/strategy
  // default (defaultPublishLanguage || language); an explicit code overrides it
  // for THIS render only. Passed straight through the existing submit `language`
  // field (server already consumes it — no backend change).
  const [reelLang, setReelLang] = useState('');
  // Brand voice — Describe mode ONLY (dead/absent in Photos mode); folds brand
  // profile + strategy into the AI scene plan. Default true.
  const [genBrandVoice, setGenBrandVoice] = useState(true);
  // Options disclosure (hook / narration / output row) — collapsed by default,
  // both video source modes (lite-first addendum §2 .dv-opts).
  const [videoOptsOpen, setVideoOptsOpen] = useState(false);
  // Drag-reorder state for the scenes rail (mirrors CreatePost's BrandRefRail
  // pattern) + which frame is being dragged (for the .is-drag ghost class).
  const frameDragIdx = useRef(null);
  const [frameDragActive, setFrameDragActive] = useState(null);
  // Last submitted video params — kept so a failed op's "Try again" (gallery
  // §B4) can reopen the generator prefilled, client-side only.
  const lastVideoSubmitRef = useRef(null);
  // GA rollout = server VIDEO_REELS_GA=1 + this default flipped (videoReelsGA
  // becomes readFlagOn). Until then, videoReelsGA() is the staged per-browser
  // tester switch: when it's on, the Reel generator opens for EVERY user
  // (server still enforces quota/402 — see visual-worker videoGaEnabled).
  // When it's off, behavior is byte-identical to the admin-only pilot.
  // Video OFF 2026-07-10: showReel = master flag only (default OFF). Dropped the
  // legacy `isAdmin && videoReelsEnabled()` (localStorage 'dathent.video.reels')
  // escape that kept video visible to admins from the old dark pilot.
  const showReel = videoReelsGA();
  const isReel = genKind === 'video' && showReel;
  const isDescribeMode = isReel && srcMode === 'describe';
  const isPhotosMode = isReel && srcMode === 'photos';
  const promptRef = useRef(null);
  const openGen = () => { if (!generationAllowed) return; setGenOpen(true); setTimeout(() => promptRef.current?.focus(), 60); };
  // §5 A-1 / §12.11 trigger honesty — the collapsed gen trigger keeps its IMAGE
  // wording and image meter unchanged (no kind-aware trigger by design); genKind
  // resets to 'image' whenever the panel closes so that card is always honest,
  // even after a Video-mode session.
  const closeGen = () => { setGenOpen(false); setGenKind('image'); };
  // Local optimistic generating tiles cover the ≤8s gap before the server poll
  // surfaces the running async_operation; the SERVER op is the source of truth
  // (it survives a reload + shows in the global Activity feed).
  const [optimistic, setOptimistic] = useState([]); // [{ id, prompt }]
  const fileRef = useRef(null);
  const logoFileRef = useRef(null); // dedicated "set your logo" upload
  const videoFileRef = useRef(null); // dedicated "upload your own video" upload

  // Image allowance for the meter. /app/brandkit is NOT the dashboard route, so
  // the provider won't auto-load entitlements — nudge it on mount (Settings
  // pattern) or the meter shows blank for paying users.
  const dash = useDashboardData() || {};
  const { entitlements, socialAccounts, ensureDashboardData, invalidateResource } = dash;
  const freePublishing = freePublishingState(dash.entitlementsError ? null : entitlements);
  const knownFree = useRef({ owner: null, value: false });
  const owner = `${user?.id || ''}:${dash.accountId || ''}`;
  if (knownFree.current.owner !== owner) knownFree.current = { owner, value: false };
  if (freePublishing) knownFree.current.value = true;
  const paidAccess = !dash.entitlementsError && ['active', 'trialing', 'grace', 'cancel_scheduled', 'downgrade_scheduled'].includes(effectiveAccessState(entitlements));
  if (paidAccess && !freePublishing) knownFree.current.value = false;
  const brandingLocked = Boolean(freePublishing) || knownFree.current.value || entitlements?.freePublishing?.active === true;
  const creationAllowed = Boolean(freePublishing) || (creationAccessState(dash) === 'allowed' && !brandingLocked);
  const brandingAllowed = creationAllowed && !brandingLocked;
  const generationAllowed = brandingAllowed;
  // Brand Kit generation shares the AI-image budget with post-image regenerations
  // (premium_visuals_monthly) so total image spend lives under one cap.
  const allowance = entitlements?.usage?.premium_visuals_monthly || null;
  // Fix list item 12: derive the lock decision from `entitlements` itself
  // (deriveBrandKitMediaLock does the same usage.premium_visuals_monthly
  // extraction as `allowance` above) so the decision is unit-testable
  // against realistic entitlements shapes, not just an already-extracted
  // allowance object.
  const imageGenerationLocked = brandingLocked || deriveBrandKitMediaLock(entitlements);

  // TASK-324 (#514): media-storage meter for the library header. The field
  // is OMITTED (not a fabricated 0) whenever the gateway could not read the
  // real cached total, so storageMeterState hides the meter in that case —
  // see src/lib/storageMeter.js.
  const storageMeter = storageMeterState(entitlements?.usage?.media_storage_bytes);

  // Server-tracked Brand Kit generations (survive reload). Render a generating
  // tile per running op; refetch the library when one finishes.
  const genStatus = useGenerationStatus();
  const bkOps = (genStatus?.operationsArr || []).filter((op) => op.kind === OPERATION_KINDS.BRANDKIT_IMAGE);
  // Reel renders are server-tracked VIDEO_RENDER ops — same shimmer machinery as
  // images, so a minutes-long render survives reload and shows in Activity.
  const reelOps = (genStatus?.operationsArr || []).filter((op) => op.kind === OPERATION_KINDS.VIDEO_RENDER);
  const runningIds = [...bkOps, ...reelOps].filter((op) => op.status === 'running').map((op) => op.id);
  const genTiles = [
    ...bkOps.filter((op) => op.status === 'running').map((op) => ({ id: op.id, prompt: op.label, kind: 'image' })),
    ...reelOps.filter((op) => op.status === 'running').map((op) => ({ id: op.id, prompt: op.label, kind: 'reel', startedAt: op.startedAt })),
    ...optimistic.filter((o) => !runningIds.includes(o.id)),
  ];
  const generating = genTiles.length > 0;
  // Video single-flight is independent of image ops (§3.2 409 generation_in_progress
  // keys on ONE video at a time) — derived from the reelOps VIDEO_RENDER tiles,
  // no new API (§12.4).
  const isVideoGenerating = reelOps.some((op) => op.status === 'running') || optimistic.some((o) => o.kind === 'reel');

  const flagOn = brandKitEnabled();
  // Issue #61 DARK gate: every typography render, mutation and media label is
  // downstream of this live-read flag. OFF keeps this screen's old path intact.
  const typographyOn = brandTypographyEnabled();

  const load = useCallback(async () => {
    try {
      // TASK-325 fix (blocker #1): the default listing now excludes 'post'
      // rows entirely (they can no longer evict native assets from the
      // shared window) — this screen's "All" tab and "From posts" chip both
      // need them, so it must opt in explicitly with origin: 'all'.
      const data = unwrap(await api.brandAssets.list(undefined, { includeVideos: true, origin: 'all' }));
      const list = Array.isArray(data?.assets) ? data.assets : [];
      // Reels carry mimeType video/* + tag 'reel' (not a brand image tag) — keep
      // that tag verbatim so they surface only under "All"; coerce image tags as before.
      // TASK-325: 'post' (auto-saved post visuals) is the SAME kind of case —
      // not one of the 5 user tags, so normalizeTag would silently rewrite it
      // to 'style' and the "From posts" chip below would have nothing to
      // filter on. Keep it verbatim, same as 'reel'.
      const mapped = list.map((a) => (
        (a.mimeType || '').startsWith('video/') || a.tag === 'post' ? a : { ...a, tag: normalizeTag(a.tag) }
      ));
      setAssets(mapped);
      setPrimaryLogoId(data?.primaryLogoId ?? null);
      return mapped;
    } catch (err) {
      // A billing block is not a loading failure: route it to the upgrade
      // flow like the video-upload handler does. (Reads are gateway-exempt
      // from the card gate now, so this is deploy-skew defense, not the norm.)
      // TASK-331: PLAN_LAPSED first — opens the renew face, not the upgrade one.
      if (err?.status === 402) { openUpgrade({ feature: 'default', variant: err?.code === 'PLAN_LAPSED' ? 'renew' : 'upgrade' }); return null; }
      setNotice({ kind: 'error', text: err?.message || t('brandkit.loadError') });
      return null;
    } finally {
      setLoading(false);
    }
  }, [t, openUpgrade]);

  useEffect(() => {
    if (!flagOn) return;
    load();
    ensureDashboardData?.();
    api.profile.getBrandProfile()
      .then((res) => {
        const profile = unwrap(res)?.brandProfile || {};
        setBrandProfile({ ...toBrandForm(profile), defaultPublishLanguage: profile.defaultPublishLanguage || null });
        if (typographyOn) setTypography(profile.typography ?? null);
      })
      .catch(() => { setBrandProfile(null); if (typographyOn) setTypography(null); });
  }, [flagOn, typographyOn, load, ensureDashboardData]);

  // When a server-tracked Brand Kit generation finishes, surface the new asset
  // (refetch) and drop its optimistic tile. seenDoneRef de-dupes per op.
  const seenDoneRef = useRef(new Set());
  useEffect(() => {
    // Both image (bkOps) and reel (reelOps) completions refetch — a finished
    // reel joins the library exactly like a finished image.
    const justDone = [...bkOps, ...reelOps].filter((op) => (op.status === 'succeeded' || op.status === 'failed') && !seenDoneRef.current.has(op.id));
    if (!justDone.length) return;
    justDone.forEach((op) => seenDoneRef.current.add(op.id));
    const failed = justDone.find((op) => op.status === 'failed');
    const failedVideo = failed && failed.kind === OPERATION_KINDS.VIDEO_RENDER;
    if (failedVideo) {
      // §6 B4 — recoverable failure: error + refund note + "Try again" action
      // that reopens the generator prefilled (retryFailedVideo, client state).
      setNotice({
        kind: 'error',
        text: `${failed.error || t('brandkit.genError')} ${t('media.videoRefunded', { defaultValue: 'Your video credit was not used.' })}`,
        action: { label: t('media.tryAgain', { defaultValue: 'Try again' }), onClick: retryFailedVideo },
      });
    } else if (failed) {
      setNotice({ kind: 'error', text: failed.error || t('brandkit.genError') });
    }
    setOptimistic((prev) => prev.filter((o) => !justDone.some((d) => d.id === o.id)));
    const succeededVideo = justDone.find((op) => op.status === 'succeeded' && op.kind === OPERATION_KINDS.VIDEO_RENDER);
    const succeeded = justDone.some((op) => op.status === 'succeeded');
    Promise.resolve(load()).then((list) => {
      // §6 B3b — the success beat after a multi-minute video wait.
      if (succeededVideo) {
        setNotice({ kind: 'success', text: t('media.videoReady', { defaultValue: 'Your video is ready' }) });
        return;
      }
      // The just-generated asset is the newest; if it carries the safety-fallback
      // flag, tell the user their references couldn't be applied (one-time info).
      if (succeeded && !failed && Array.isArray(list) && list[0]?.referencesDropped) {
        setNotice({ kind: 'info', text: t('brandkit.refsDroppedNotice') });
      }
    });
    invalidateResource?.('entitlements');
  }, [bkOps, reelOps, load, invalidateResource, t]);

  // GA cinematic default: once entitlements load, default the video quality to
  // cinematic when the plan has it available (free/trial 0 quota stay on
  // Slideshow). Never overrides a manual pick (reelQualityTouchedRef).
  useEffect(() => {
    if (!videoCinematicEnabled()) return;
    const cq = videoQuotaInfo(entitlements?.usage?.video_cinematic_monthly);
    if (cq.known && cq.available && !reelQualityTouchedRef.current) setReelQuality('cinematic');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitlements]);

  if (!flagOn) return <Navigate to="/app/plan" replace />;

  const isVideoAsset = (a) => (a.mimeType || '').startsWith('video/');
  // Videos filter chip (§6 B1) — video assets carry tag 'reel', which matches
  // no image bucket, so they were invisible outside "All"; a dedicated virtual
  // filter value surfaces them.
  const markedAssetIds = typographyOn && Array.isArray(typography?.markedAssetIds) ? typography.markedAssetIds : [];
  const markedAssetSet = new Set(markedAssetIds);
  const visible = activeTag === 'all'
    ? assets
    : activeTag === 'videos'
      ? assets.filter(isVideoAsset)
      : activeTag === 'font'
        ? assets.filter((asset) => markedAssetSet.has(asset.id))
        // TASK-325: "From posts" — a separate axis from the 5 brand tags,
        // same virtual-filter pattern as videos/font above (over the single
        // already-fetched asset list, no extra request).
        : activeTag === 'posts'
          ? assets.filter((a) => a.tag === 'post')
          : assets.filter((a) => a.tag === activeTag);
  const counts = assets.reduce((m, a) => ({ ...m, [a.tag]: (m[a.tag] || 0) + 1 }), {});
  const videoCount = assets.filter(isVideoAsset).length;
  const postOriginCount = assets.filter((a) => a.tag === 'post').length;
  const primaryLogo = assets.find((a) => a.id === primaryLogoId) || null;
  const brandName = brandProfile?.businessName || brandProfile?.name || '';
  const brandColors = Array.isArray(brandProfile?.brandColors) ? brandProfile.brandColors.filter(Boolean).slice(0, 6) : [];
  const typographyLanguages = collectTypographyLanguages(brandProfile, socialAccounts || []);
  const typographyLangsHash = typographyLanguages.join('|');
  const typographyDefaultAsset = assets.find((asset) => asset.id === typography?.defaultAssetId) || null;

  const saveTypography = async (next) => {
    if (!typographyOn || !brandingAllowed) return;
    const previous = typography;
    setTypography(next);
    setFontError(null);
    try {
      await api.profile.updateBrandProfile({ typography: next });
      api.invalidateCache?.('/api/brand-profile');
    } catch (error) {
      setTypography(previous);
      setFontError(error?.message || t('typography.saveError'));
    }
  };

  const typographyForAsset = (asset) => {
    const ids = [...new Set([...markedAssetIds, asset.id])];
    return {
      enabled: typography?.enabled === true,
      defaultAssetId: asset.id,
      markedAssetIds: ids,
      font: typography?.font || {
        family: (asset.fileName || t('typography.mediaReference')).replace(/\.[^.]+$/, ''),
        source: 'media',
        coverage: [],
      },
      langsHash: typographyLangsHash,
    };
  };

  const setTypographyDefault = (asset) => saveTypography(typographyForAsset(asset));
  const removeTypographyReference = (asset) => {
    const remaining = markedAssetIds.filter((id) => id !== asset.id);
    if (!remaining.length) { saveTypography(null); return; }
    const defaultAssetId = typography?.defaultAssetId === asset.id ? remaining[0] : typography?.defaultAssetId;
    saveTypography({ ...typography, defaultAssetId, markedAssetIds: remaining, langsHash: typographyLangsHash });
  };

  const confirmCatalogFont = async (font) => {
    if (!typographyOn || !brandingAllowed || fontRendering) return;
    setFontRendering(true);
    setFontError(null);
    try {
      const glyphLines = specimenGlyphs(typographyLanguages);
      await loadGoogleFont(font.family, `${brandName || t('typography.yourBrand')} ${glyphLines.join(' ')}`);
      const dataUrl = renderTypographySpecimen({
        family: font.family,
        brandName: brandName || t('typography.yourBrand'),
        languages: typographyLanguages,
        brandColors,
      });
      const fileName = `${font.family.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-font-reference.png`;
      const created = unwrap(await trackUpload(fileName, (onProgress) => api.brandAssets.upload({ dataUrl, fileName, tag: 'style', onProgress })));
      const asset = created?.asset ?? created;
      if (!asset?.id) throw new Error(t('typography.uploadError'));
      const next = {
        enabled: typography?.enabled === true,
        defaultAssetId: asset.id,
        markedAssetIds: [...new Set([...markedAssetIds, asset.id])],
        font: { family: font.family, source: 'catalog', coverage: font.coverage },
        langsHash: typographyLangsHash,
      };
      await api.profile.updateBrandProfile({ typography: next });
      api.invalidateCache?.('/api/brand-profile');
      setTypography(next);
      await load();
      setFontCatalogOpen(false);
    } catch (error) {
      setFontError(error?.message || t('typography.renderError'));
    } finally {
      setFontRendering(false);
    }
  };

  // TASK-324 (#514): the visual-worker's storage gate (assertStorageCapacity,
  // dark unless STORAGE_ENFORCEMENT_ENABLED) 402s with
  // { code: 'QUOTA_EXCEEDED', details: { metric: 'media_storage_bytes' } } —
  // the SAME QUOTA_EXCEEDED shape as social/brand-profile caps, but a
  // generic "upgrade" prompt is the wrong response to "you're out of space"
  // (upgrading doesn't help someone who just needs to delete something).
  // Returns the localized storage-full string, or null for every other error
  // so callers fall through to their existing handling untouched.
  const mediaStorageFullMessage = (err) => (
    err?.status === 402 && err?.details?.metric === 'media_storage_bytes'
      ? t('brandkit.storageFull')
      : null
  );

  const uploadFiles = async (fileList) => {
    if (!creationAllowed) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setNotice(null);
    setUploading(true);
    let failed = 0;
    for (const file of files) {
      let up = file;
      if (isSvgFile(file)) {
        // Convert SVG → PNG in the browser; raw SVG is rejected server-side.
        try { up = await rasterizeSvgToPng(file); }
        catch { failed += 1; setNotice({ kind: 'error', text: t('brandkit.errSvg') }); continue; }
      } else if (!ACCEPT_SET.has(file.type)) {
        failed += 1; setNotice({ kind: 'error', text: t('brandkit.errType') }); continue;
      }
      if (up.size > MAX_BYTES) { failed += 1; setNotice({ kind: 'error', text: t('brandkit.errSize') }); continue; }
      try {
        const dataUrl = await readAsDataUrl(up);
        // New uploads inherit the active filter tag (or 'style' on "All") — a
        // single tag control instead of a separate "Upload as" row; the per-
        // asset tag can still be changed in the detail modal.
        const newTag = activeTag !== 'all' && TAG_SET.has(activeTag) && !(brandingLocked && activeTag === 'logo') ? activeTag : 'style';
        await trackUpload(up.name, (onProgress) => api.brandAssets.upload({ dataUrl, fileName: up.name, tag: newTag, onProgress }), { size: up.size });
      } catch (e) {
        failed += 1;
        setNotice({ kind: 'error', text: mediaStorageFullMessage(e) || e?.message || t('brandkit.uploadError') });
      }
    }
    await load();
    setUploading(false);
    if (!failed) setNotice(null);
    // TASK-324: every successful iteration above added bytes to the STOCK
    // media_storage_bytes counter server-side — refresh so the header meter
    // (and any other entitlements-driven UI) reflects it without a poll.
    invalidateResource?.('entitlements');
  };

  const onPick = (e) => { uploadFiles(e.target.files); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); };

  // "Set your logo" — upload an image AND make it the primary logo in one step
  // (the most intuitive path; the design's identity-strip + grid-star both lead
  // here). Tags the asset 'logo' regardless of the active filter.
  const onPickLogo = async (e) => {
    if (!brandingAllowed) return;
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    let up = file;
    if (isSvgFile(file)) {
      try { up = await rasterizeSvgToPng(file); }
      catch { setNotice({ kind: 'error', text: t('brandkit.errSvg') }); return; }
    } else if (!ACCEPT_SET.has(file.type)) {
      setNotice({ kind: 'error', text: t('brandkit.errType') }); return;
    }
    if (up.size > MAX_BYTES) { setNotice({ kind: 'error', text: t('brandkit.errSize') }); return; }
    setUploading(true);
    setNotice(null);
    try {
      const dataUrl = await readAsDataUrl(up);
      const created = unwrap(await trackUpload(up.name, (onProgress) => api.brandAssets.upload({ dataUrl, fileName: up.name, tag: 'logo', onProgress }), { size: up.size }));
      const newId = created?.asset?.id ?? created?.id ?? null;
      if (newId) await api.brandAssets.setPrimaryLogo(newId);
      const refreshed = await load();
      const logoUrl = refreshed?.find((asset) => asset.id === newId)?.url || created?.asset?.url;
      if (newId && logoUrl) window.dispatchEvent(new CustomEvent(PRIMARY_LOGO_UPDATED_EVENT, { detail: { url: logoUrl } }));
      invalidateResource?.('profile');
      invalidateResource?.('entitlements'); // TASK-324: logo upload also spends storage bytes
    } catch (err) {
      setNotice({ kind: 'error', text: mediaStorageFullMessage(err) || err?.message || t('brandkit.uploadError') });
    } finally {
      setUploading(false);
    }
  };

  // "Upload your own video" — client-side validation (type/size/duration) BEFORE
  // spending an upload, then api.videos.upload → api.videos.setPoster (best-
  // effort) → refetch so the new tile appears in the grid (and under the Videos
  // filter). Independent of reel GENERATION (showReel/videoReelsGA), which stays
  // untouched — this only posts an already-made file.
  const uploadVideo = async (file) => {
    if (!creationAllowed) return;
    if (!file || uploadingVideo) return;
    setNotice(null);
    const looksLikeMp4OrMov = VIDEO_ACCEPT_SET.has(file.type) || /\.(mp4|mov)$/i.test(file.name || '');
    if (!looksLikeMp4OrMov) {
      setNotice({ kind: 'error', text: t('media.videoErrType', { defaultValue: 'Upload an MP4 or MOV video.' }) });
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      setNotice({ kind: 'error', text: t('media.videoErrSize', { defaultValue: 'Video is too large — max 100 MB.' }) });
      return;
    }
    setUploadingVideo(true);
    try {
      let meta;
      try {
        meta = await readVideoMetaAndPoster(file);
      } catch (_) {
        setNotice({ kind: 'error', text: t('media.videoErrRead', { defaultValue: "Couldn't read that video file." }) });
        return;
      }
      if (Number.isFinite(meta.duration) && meta.duration > MAX_VIDEO_SECONDS) {
        setNotice({ kind: 'error', text: t('media.videoErrLength', { defaultValue: 'Video is too long — max 15 minutes.' }) });
        return;
      }
      let created;
      try {
        created = unwrap(await trackUpload(file.name, (onProgress) => api.videos.upload(file, {
          onProgress,
          fileName: file.name,
          width: meta.width || undefined,
          height: meta.height || undefined,
          durationSeconds: Number.isFinite(meta.duration) ? Math.round(meta.duration) : undefined,
        }), { size: file.size }));
      } catch (err) {
        // TASK-324: a storage-full 402 is not a plan-upgrade situation (this
        // upload path is already paid-plan-only) — check that specific metric
        // BEFORE the generic upgrade-modal fallback for every other 402.
        const storageFull = mediaStorageFullMessage(err);
        if (storageFull) { setNotice({ kind: 'error', text: storageFull }); return; }
        // TASK-331: PLAN_LAPSED first — opens the renew face, not the upgrade one.
        if (err?.status === 402) { openUpgrade({ feature: 'default', variant: err?.code === 'PLAN_LAPSED' ? 'renew' : 'upgrade' }); return; }
        setNotice({ kind: 'error', text: err?.message || t('media.videoUploadError', { defaultValue: 'Video upload failed. Try again.' }) });
        return;
      }
      const asset = created?.asset ?? created;
      if (asset?.id && meta.posterDataUrl) {
        // Non-fatal — the video still plays/lists fine without a poster (the
        // <video preload="metadata"> element paints its own first frame).
        try { await api.videos.setPoster(asset.id, meta.posterDataUrl); }
        catch (e) { console.warn('[BrandKit] video poster upload failed', e); }
      }
      invalidateResource?.('entitlements'); // TASK-324: video upload also spends storage bytes
      await load();
    } finally {
      setUploadingVideo(false);
    }
  };
  const onPickVideo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    uploadVideo(file);
  };

  // Generate an AI image into the library. An optimistic shimmer tile appears
  // in the grid while the request is in flight; on completion load() surfaces
  // the real asset and the tile is removed. (Does not survive refresh — a
  // few-second op; the server-tracked variant is a later upgrade.)
  const doGenerate = async () => {
    if (!generationAllowed) return;
    const prompt = genPrompt.trim();
    if (generating) return; // one at a time (the server enforces this too)
    if (isReel) {
      // Video mode — two mutually exclusive submit shapes (lite-first addendum
      // §1/§6.1). Describe: { prompt, brandVoice, narration, script, hookLine,
      // language }, NO frameAssetIds. Photos: exactly today's body (frameAssetIds
      // + narration/script/hookLine/language). Never send the inactive mode's fields.
      // reelLang (per-video picker) wins over the brand/strategy default.
      const language = reelLang || brandProfile?.defaultPublishLanguage || brandProfile?.language || undefined;
      let videoAllow = entitlements?.usage?.video_reels_monthly;
      let submitBody;
      let echoLabel;
      if (isDescribeMode) {
        if (prompt.length < 3) {
          setNotice({ kind: 'error', text: t('media.describeMin', { defaultValue: 'Describe the video in a few words first.' }) });
          return;
        }
        submitBody = {
          prompt,
          brandVoice: genBrandVoice,
          narration: reelNarration,
          script: reelScript.trim() || undefined,
          hookLine: reelHook.trim() || undefined,
          language,
          // Optional "1 click with a reference" — describe mode only; the
          // backend conditions every generated scene on these owned photos.
          referenceAssetIds: describeRefs.length ? describeRefs.map((r) => r.id) : undefined,
          // Tier-2 cinematic (admin pilot) — describe mode only; server double-
          // gates (VIDEO_CINEMATIC_ENABLED + admin). Omitted otherwise = standard
          // slideshow, byte-identical to today.
          quality: (videoCinematicEnabled() && reelQuality === 'cinematic') ? 'cinematic' : undefined,
        };
        echoLabel = reelHook.trim() || prompt;
      } else {
        // Photos mode — the scenes rail is the required source.
        if (pickedRefs.length === 0) {
          setNotice({ kind: 'error', text: t('media.videoNeedsFrames', { defaultValue: 'Pick at least one image to build the video from.' }) });
          return;
        }
        submitBody = {
          frameAssetIds: pickedRefs.map((r) => r.id),
          narration: reelNarration,
          script: reelScript.trim() || undefined,
          hookLine: reelHook.trim() || undefined,
          language,
        };
        echoLabel = reelHook.trim() || t('media.videoLabel', { defaultValue: 'Video' });
      }
      if (videoAllow && videoAllow.limit != null && (videoAllow.remaining ?? 0) <= 0) {
        setNotice({ kind: 'error', text: t('media.videoLimit', { defaultValue: 'Monthly video limit reached.' }) });
        return;
      }
      setNotice(null);
      // Snapshot the FULL picked-ref objects ({id,url,tag}) — not just the ids in
      // submitBody — so a failed photos-mode submit's "Try again" can repaint the
      // scenes rail after the 202 path clears pickedRefs below (§6 B4). Same for
      // describeRefs (describe-mode reference photos).
      lastVideoSubmitRef.current = { srcMode, submitBody, echoLabel, pickedRefs: [...pickedRefs], describeRefs: [...describeRefs] };
      try {
        const res = unwrap(await api.brandAssets.generateVideo(submitBody));
        const opId = res?.operationId;
        if (opId) {
          setOptimistic((prev) => [{ id: opId, prompt: echoLabel, kind: 'reel' }, ...prev]);
          setTimeout(() => setOptimistic((prev) => prev.filter((o) => o.id !== opId)), 12000);
          closeGen();
          setPickedRefs([]);
          setDescribeRefs([]);
          setReelHook('');
          setReelScript('');
        }
        setGenPrompt('');
      } catch (e) {
        // GH-1133 S5 (§2.5): a free-preview 402 after this click opens the
        // trial wall for its reason (only preview orgs get details.preview).
        if (isPreviewBlocked(e)) { openUpgrade({ feature: wallSourceForReason(e.details.preview.reason) }); return; }
        if (e?.code === 'PLAN_LAPSED') {
          invalidateResource?.('entitlements', { fresh: true });
          openUpgrade({ feature: 'default', variant: 'renew' });
        }
        setNotice({ kind: 'error', text: e?.code === 'PLAN_LAPSED' ? t('paymentIssue.lapsedBody') : e?.message || t('brandkit.genError') });
      }
      return;
    }
    if (prompt.length < 3) { setNotice({ kind: 'error', text: t('brandkit.genTooShort') }); return; }
    if (allowance && allowance.limit != null && (allowance.remaining ?? 0) <= 0) {
      setNotice({ kind: 'error', text: t('brandkit.genLimit') });
      return;
    }
    setNotice(null);
    try {
      // Aspect → real pixel dims (backend accepts width/height); style + on-brand
      // colors fold into the prompt — no backend change needed.
      const dims = { square: [1024, 1024], portrait: [1024, 1280], landscape: [1280, 1024] }[genAspect] || [1024, 1024];
      const styleFrag = STYLE_FRAG[genStyle] || '';
      const stylePrefix = styleFrag ? `${styleFrag} ` : '';
      // The gateway is the single brand on/off decision (mirrors New Post). No
      // brand profile → always brand-free.
      const onBrand = !!brandProfile && genBrandAware;
      // Brand-free art direction: each chosen control folds into the prompt as a
      // short clause, exactly like stylePrefix — the controls "actually work"
      // because they steer gpt-image, with NO backend change.
      let artDir = '';
      if (!onBrand) {
        const frags = [];
        if (genMood && MOOD_FRAG[genMood]) frags.push(MOOD_FRAG[genMood]);
        if (genLighting && LIGHT_FRAG[genLighting]) frags.push(LIGHT_FRAG[genLighting]);
        if (genComposition && COMP_FRAG[genComposition]) frags.push(COMP_FRAG[genComposition]);
        if (genPalette.length) {
          // "warm gold (#D4A53F)" reads far better to the model than bare hex.
          const named = genPalette.map((h) => { const n = describeColor(h); return n ? `${n} (${h})` : h; });
          frags.push(`a colour palette dominated by ${named.join(', ')}`);
        }
        if (frags.length) artDir += ` ${frags.join('. ')}.`;
        // Brand-free = no AUTO branding: don't let the model INVENT a fake logo,
        // watermark, or caption. But LEAD with the permission — a prohibition-first
        // phrasing ("do not render any text…") made gpt-image drop even explicitly
        // requested text (the owner asked for the brand name and got none). So
        // render what's asked for, and only forbid UNREQUESTED extras.
        artDir += ' Render any text, brand name, or logo that the description above explicitly asks for (spell it exactly); do not add any other brand name, logo, watermark, or caption on your own.';
      }
      // Brand-aware: fold the real brand identity (name, business, tone, palette,
      // language) into the prompt so the image looks like THIS brand, not random
      // stock — grounded in the same profile the content pipeline uses.
      let brandCtx = '';
      if (onBrand) {
        const langName = (() => {
          const code = brandProfile.language || brandProfile.defaultPublishLanguage;
          if (!code) return null;
          try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(String(code).slice(0, 2)) || code; } catch (_) { return code; }
        })();
        const parts = [];
        const desc = (brandProfile.businessDescription || '').trim();
        const tone = (brandProfile.toneOfVoice || brandProfile.customToneDescription || '').trim();
        const positioning = (brandProfile.positioning || '').trim();
        const audience = (brandProfile.targetAudience || '').trim();
        const pillars = (Array.isArray(brandProfile.contentPillars) ? brandProfile.contentPillars : [])
          .map((p) => (typeof p === 'string' ? p : p?.name)).filter(Boolean).slice(0, 3);
        // Descriptive style grounding only — NO literal brand name / "on-brand"
        // phrases (those used to get painted into the image as text).
        if (desc) parts.push(`a business described as: ${desc.slice(0, 160)}`);
        if (positioning) parts.push(`positioned as ${positioning.slice(0, 120)}`);
        if (audience) parts.push(`for an audience of ${audience.slice(0, 100)}`);
        if (tone) parts.push(`visual tone ${tone}`);
        if (pillars.length) parts.push(`recurring themes ${pillars.join(', ')}`);
        if (brandColors.length) parts.push(`drawing on the brand colours ${brandColors.join(', ')}`);
        if (parts.length) brandCtx += ` Make the image visually consistent with ${parts.join('; ')}.`;
        // Hard guard: the brand context is guidance, not caption text. Image
        // models otherwise auto-paint phrases like "on-brand" or the style words
        // into the picture. But the brand name itself must render WHEN ASKED —
        // a blanket "do NOT render the brand name" suppressed even explicit
        // requests. So: render requested text (incl. the brand name) and only
        // block the style guidance from leaking in as text.
        brandCtx += ' Render text in the image only when the description above explicitly asks for it (including the brand name, spelled exactly, if requested); never paint this style guidance or the words "on-brand"/"brand" into the image as text on your own';
        if (langName) brandCtx += `, written in ${langName}`;
        brandCtx += '.';
      }
      const finalPrompt = `${stylePrefix}${prompt}${artDir}${brandCtx}`.trim();
      // Backgrounded server-side: returns an operationId immediately. The
      // running op (and its tile) is then driven by the global status poll, so
      // it survives a reload and the completion handler refetches the library.
      const res = unwrap(await api.brandAssets.generate({ prompt: finalPrompt, tag: 'style', width: dims[0], height: dims[1], referenceAssetIds: pickedRefs.map((r) => r.id) }));
      const opId = res?.operationId;
      if (opId) {
        setOptimistic((prev) => [{ id: opId, prompt }, ...prev]);
        // The optimistic tile only bridges the gap before the server poll
        // surfaces the running op. Auto-expire it so a stuck/stale op can never
        // leave an infinite local spinner — after this the tile is server-driven
        // (a running op keeps it; completion or the 10-min stale backstop clears it).
        setTimeout(() => setOptimistic((prev) => prev.filter((o) => o.id !== opId)), 12000);
        // Generation is now running in the library (shimmer tile). Collapse the
        // form so attention moves to the result instead of the form sitting open
        // looking inert; clear the per-image inputs (the queued job captured them).
        setGenOpen(false);
        setPickedRefs([]);
      }
      setGenPrompt('');
    } catch (e) {
      // GH-1133 S5 (§2.5): a free-preview 402 after this click opens the
      // trial wall for its reason (only preview orgs get details.preview).
      if (isPreviewBlocked(e)) { openUpgrade({ feature: wallSourceForReason(e.details.preview.reason) }); return; }
      if (e?.code === 'PLAN_LAPSED') {
        invalidateResource?.('entitlements', { fresh: true });
        openUpgrade({ feature: 'default', variant: 'renew' });
      }
      setNotice({ kind: 'error', text: e?.code === 'PLAN_LAPSED' ? t('paymentIssue.lapsedBody') : e?.message || t('brandkit.genError') });
    }
  };

  // Gallery §B4 "Try again" — reopens the generator prefilled from the last
  // failed video submit's params (client-side state, kept in lastVideoSubmitRef).
  // Every submitted field round-trips: srcMode, prompt+brandVoice (describe),
  // the full picked-ref objects (photos — the 202 path cleared pickedRefs, so
  // the id-only submitBody could not repaint the rail), narration, script, hook.
  const retryFailedVideo = () => {
    const last = lastVideoSubmitRef.current;
    if (!last) { setGenKind('video'); setGenOpen(true); return; }
    setGenKind('video');
    setSrcMode(last.srcMode);
    if (last.srcMode === 'describe') {
      setGenPrompt(last.submitBody.prompt || '');
      setGenBrandVoice(last.submitBody.brandVoice !== false);
      setDescribeRefs(Array.isArray(last.describeRefs) ? last.describeRefs : []);
    } else {
      setPickedRefs(Array.isArray(last.pickedRefs) ? last.pickedRefs : []);
    }
    setReelNarration(last.submitBody.narration !== false);
    setReelScript(last.submitBody.script || '');
    setReelHook(last.submitBody.hookLine || '');
    setGenOpen(true);
    setTimeout(() => promptRef.current?.focus(), 60);
  };

  const mutate = async (fn) => {
    try {
      await fn();
      await load();
    } catch (e) {
      setNotice({ kind: 'error', text: e?.message || t('brandkit.actionError') });
    }
  };

  const retag = (asset, tag) => mutate(async () => {
    if (!brandingAllowed) return;
    setBusyId(asset.id);
    try { await api.brandAssets.updateTag(asset.id, tag); setDetail((d) => (d && d.id === asset.id ? { ...d, tag } : d)); }
    finally { setBusyId(null); }
  });

  const makePrimary = (asset) => mutate(async () => {
    if (!brandingAllowed) return;
    setBusyId(asset.id);
    try {
      await api.brandAssets.setPrimaryLogo(asset.id);
      window.dispatchEvent(new CustomEvent(PRIMARY_LOGO_UPDATED_EVENT, { detail: { url: asset.url } }));
    }
    finally { setBusyId(null); }
  });

  // Grid star — promote ANY asset to the primary logo. If it wasn't tagged
  // 'logo' we retag it first, so "upload any image, mark it as logo" works
  // straight from the card (the user's expectation), then set it primary.
  const setAsLogo = (asset) => mutate(async () => {
    if (!brandingAllowed) return;
    setBusyId(asset.id);
    try {
      if (asset.tag !== 'logo') await api.brandAssets.updateTag(asset.id, 'logo');
      await api.brandAssets.setPrimaryLogo(asset.id);
      window.dispatchEvent(new CustomEvent(PRIMARY_LOGO_UPDATED_EVENT, { detail: { url: asset.url } }));
      invalidateResource?.('profile');
    } finally { setBusyId(null); }
  });

  // Reference-images row (IMAGE mode only) — one .mg-ctl rendered inside
  // whichever brand group is active (after Style). References apply regardless
  // of brand on/off; the row inherits the group's accent so it never looks foreign.
  const renderRefRow = () => (
    <div className="mg-ctl mg-refrow">
      <div className="mg-ml">
        <span className="mg-t">{t('brandkit.refImages')}</span>
        <span className="mg-opt">{t('brandkit.refOptional')}</span>
      </div>
      <div className="mg-ref-rail">
        {pickedRefs.map((r) => (
          <span key={r.id} className="mg-ref" style={{ backgroundImage: `url(${r.url})` }}>
            <button type="button" className="mg-ref-x" onClick={() => setPickedRefs((p) => p.filter((x) => x.id !== r.id))} aria-label={t('brandkit.removeRef')}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M6 6 18 18M18 6 6 18" /></svg>
            </button>
          </span>
        ))}
        {pickedRefs.length < MAX_GEN_REFS && (
          <button type="button" className="mg-refadd" onClick={() => setRefPickerOpen(true)} aria-label={t('brandkit.addRef')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            <span className="mg-refadd-cap">{t('brandkit.add')}</span>
          </button>
        )}
      </div>
      <div className="mg-refhelp"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>{t('brandkit.refHelp')}</div>
    </div>
  );

  // Reference-photos row (VIDEO describe-mode ONLY, "1 click with a reference")
  // — same compact recipe as renderRefRow (image mode), a SEPARATE picked-set
  // (describeRefs) so it never fights photos-mode's scenes rail or image-mode's
  // own refs for state. Fully optional — describe stays 1-click without it;
  // this is one quiet row, not a required step.
  const renderDescribeRefRow = () => (
    <div className="mg-ctl mg-refrow">
      <div className="mg-ml">
        <span className="mg-t">{t('media.describeRefs', { defaultValue: 'Reference photos' })}</span>
        <span className="mg-opt">{t('brandkit.refOptional')}</span>
      </div>
      <div className="mg-ref-rail">
        {describeRefs.map((r) => (
          <span key={r.id} className="mg-ref" style={{ backgroundImage: `url(${r.url})` }}>
            <button type="button" className="mg-ref-x" onClick={() => setDescribeRefs((p) => p.filter((x) => x.id !== r.id))} aria-label={t('brandkit.removeRef')}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M6 6 18 18M18 6 6 18" /></svg>
            </button>
          </span>
        ))}
        {describeRefs.length < MAX_GEN_REFS && (
          <button type="button" className="mg-refadd" onClick={() => setDescribeRefPickerOpen(true)} aria-label={t('brandkit.addRef')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            <span className="mg-refadd-cap">{t('brandkit.add')}</span>
          </button>
        )}
      </div>
      <div className="mg-refhelp">{t('media.describeRefHelp', { defaultValue: 'Throw in a product or face photo — the AI-generated scenes will feature it.' })}</div>
    </div>
  );

  // Scenes rail (VIDEO photos-mode only, §5 A1) — the required source; portrait
  // 9:16 thumbs (.dv-frame) with glass order badges + "opens" on the first (the
  // hook scene). Drag-reorder mirrors CreatePost's BrandRefRail pattern; keyboard
  // arrow-keys reorder too (canonical a11y: "Frame N of M. Arrow keys to reorder.").
  const moveFrame = (from, to) => {
    if (to < 0 || to >= pickedRefs.length || from === to) return;
    setPickedRefs((prev) => {
      const arr = [...prev];
      const [it] = arr.splice(from, 1);
      arr.splice(to, 0, it);
      return arr;
    });
  };
  const renderScenesRow = () => (
    <div className="mg-ctl mg-refrow">
      <div className="mg-ml">
        <span className="mg-t">{t('media.videoFrames', { defaultValue: 'Scenes' })}</span>
        <span className="mg-hint-r">{t('media.videoFramesHint', { defaultValue: `Pick 1–${MAX_REEL_FRAMES} images` })}</span>
      </div>
      <div className="mg-ref-rail">
        {pickedRefs.map((r, i) => (
          <div
            key={r.id}
            className={`mg-ref dv-frame ${frameDragActive === i ? 'is-drag' : ''}`}
            style={{ backgroundImage: `url(${r.url})` }}
            tabIndex={0}
            role="option"
            aria-label={t('media.frameAria', { defaultValue: `Frame ${i + 1} of ${pickedRefs.length}. Arrow keys to reorder.`, n: i + 1, total: pickedRefs.length })}
            draggable
            onDragStart={() => { frameDragIdx.current = i; setFrameDragActive(i); }}
            onDragOver={(e) => { e.preventDefault(); if (frameDragIdx.current != null && frameDragIdx.current !== i) { moveFrame(frameDragIdx.current, i); frameDragIdx.current = i; setFrameDragActive(i); } }}
            onDragEnd={() => { frameDragIdx.current = null; setFrameDragActive(null); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); moveFrame(i, i - 1); }
              else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); moveFrame(i, i + 1); }
              else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); setPickedRefs((p) => p.filter((x) => x.id !== r.id)); }
            }}
          >
            <span className="ord">{i + 1}</span>
            {i === 0 && <span className="opens">{t('media.videoOpens', { defaultValue: 'opens' })}</span>}
            <button type="button" className="mg-ref-x" onClick={() => setPickedRefs((p) => p.filter((x) => x.id !== r.id))} aria-label={t('brandkit.removeRef')}>
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M6 6 18 18M18 6 6 18" /></svg>
            </button>
          </div>
        ))}
        {pickedRefs.length < MAX_REEL_FRAMES && (
          <button type="button" className="mg-refadd dv-frame" onClick={() => setRefPickerOpen(true)} aria-label={t('brandkit.addRef')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            <span className="mg-refadd-cap">{t('brandkit.add')}</span>
          </button>
        )}
      </div>
      <div className="mg-refhelp">{t('media.videoFramesHelp', { defaultValue: 'Each image becomes a scene — plays in order, about 3–6 seconds each. Drag to reorder.' })}</div>
    </div>
  );

  // Reel narration DEFAULTS to the account publish language (defaultPublishLanguage
  // first, matching content-worker's publish-language chain 0040), but the user
  // can now override it per-video via the picker below (reelLang).
  const reelDefaultLangCode = brandProfile?.defaultPublishLanguage || brandProfile?.language || null;
  const reelLangName = (() => {
    if (!reelDefaultLangCode) return null;
    try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(String(reelDefaultLangCode).slice(0, 2)) || reelDefaultLangCode; } catch (_) { return reelDefaultLangCode; }
  })();
  // Languages we ship native TTS voices for (run-video.ts AZURE_VOICES) — the
  // picker is limited to these so a chosen language always narrates in a native
  // voice, never an en voice reading foreign text. Labels use each language's
  // own autonym so they read naturally regardless of UI locale.
  const NARRATION_LANGS = ['uk', 'ru', 'en', 'de', 'es', 'pl', 'cs', 'lt', 'lv', 'sv'];
  const langAutonym = (code) => {
    try {
      const name = new Intl.DisplayNames([code], { type: 'language' }).of(code) || code;
      return name.charAt(0).toUpperCase() + name.slice(1);
    } catch (_) { return code; }
  };

  // Image mode → the free-form aspect switcher (BYTE-IDENTICAL to today).
  const renderAspectRow = () => {
    return (
      <div className="mg-ctl">
        <div className="mg-ml"><span className="mg-t">{t('brandkit.aspect')}</span></div>
        <div className="bk-aspect">
          {[['square', 11, 11], ['portrait', 9, 12], ['landscape', 13, 9]].map(([a, w, h]) => (
            <button key={a} type="button" className={genAspect === a ? 'on' : ''} onClick={() => setGenAspect(a)}><i style={{ width: w, height: h, border: '1.5px solid currentColor', borderRadius: 2, display: 'block' }} />{t(`brandkit.aspect_${a}`)}</button>
          ))}
        </div>
      </div>
    );
  };

  // Video mode — the .dv-opts disclosure contents (lite-first addendum §2):
  // hook card · narration chips + .dv-narr reveal (language strip, voice hint,
  // .dv-script) · output row (.dv-aspects lock chip + describe-mode-only
  // "4 scenes · auto" info chip + .dv-dest "Publishes to"). Shared by BOTH
  // source modes; hook placeholder + silent hint are narration/hook-aware.
  const renderVideoOpts = () => {
    const hookWords = reelHook.trim() ? reelHook.trim().split(/\s+/).length : 0;
    const silentHint = reelHook.trim()
      ? t('media.silentHint', { defaultValue: 'Silent video — your hook appears on screen.' })
      : t('media.silentHintNoHook', { defaultValue: 'Silent video — add a hook line so text appears on screen.' });
    return (
      <>
        {/* Hook card (pack .dv-hook) — the bold on-screen line of the first 3
            seconds. Empty = AI writes it (narration ON only); filled = verbatim. */}
        <div className="dv-hook">
          <div className="dv-hook-top">
            <span className="dv-hook-t">{t('media.hookTitle', { defaultValue: 'Hook — the first 3 seconds' })}</span>
            <span className="dv-hook-badge">{t('media.hookBadge', { defaultValue: '0–3s' })}</span>
            {hookWords > 0 && (
              <span className={`dv-hook-c ${hookWords > 9 ? 'is-over' : ''}`} aria-live="polite">{hookWords}/9</span>
            )}
          </div>
          <input
            type="text"
            className="dv-hook-field"
            value={reelHook}
            maxLength={90}
            onChange={(e) => setReelHook(e.target.value)}
            placeholder={reelNarration
              ? t('media.hookPlaceholder', { defaultValue: 'Leave empty — AI writes a scroll-stopping hook' })
              : t('media.hookPlaceholderSilent', { defaultValue: "Add a hook line — it's the only text on screen." })}
          />
          <span className="dv-hook-hint">{t('media.hookHint', { defaultValue: 'One bold line, max 9 words: a number, a mistake, a question — what makes them stop.' })}</span>
        </div>
        <div className="nvbk-div" />

        {/* Narration — progressive disclosure. */}
        <div className="mg-ctl">
          <div className="mg-ml"><span className="mg-t">{t('media.narration', { defaultValue: 'Narration' })}</span></div>
          <div className="bk-chips">
            <button type="button" className={`bk-chip ${reelNarration ? 'on' : ''}`} aria-pressed={reelNarration} onClick={() => setReelNarration(true)}>{t('media.narrationOn', { defaultValue: 'Voiceover' })}</button>
            <button type="button" className={`bk-chip ${!reelNarration ? 'on' : ''}`} aria-pressed={!reelNarration} onClick={() => setReelNarration(false)}>{t('media.narrationOff', { defaultValue: 'No voiceover' })}</button>
          </div>
          {reelNarration ? (
            <div className="dv-narr">
              {/* Per-video language picker — defaults to the strategy language,
                  overridable just for this render. Replaces the old read-only
                  "Narrated in X" + static "uk/ru/en" voice hint. */}
              <div className="dv-lang">
                <span className="dv-lang-l">{t('media.videoLanguage', { defaultValue: 'Voiceover language' })}</span>
                <select
                  className="dv-lang-sel"
                  value={reelLang}
                  onChange={(e) => setReelLang(e.target.value)}
                  aria-label={t('media.videoLanguage', { defaultValue: 'Voiceover language' })}
                >
                  <option value="">{reelLangName
                    ? t('media.langFromStrategy', { defaultValue: `From your strategy · ${reelLangName}`, language: reelLangName })
                    : t('media.langFromStrategyGeneric', { defaultValue: 'From your strategy' })}</option>
                  {NARRATION_LANGS.map((code) => (
                    <option key={code} value={code}>{langAutonym(code)}</option>
                  ))}
                </select>
                <span className="dv-lang-hint">{t('media.videoLanguageHint', { defaultValue: 'Defaults to your strategy language — change it just for this video.' })}</span>
              </div>
              <div className="dv-script">
                <div className="mg-ml"><span className="mg-t">{t('media.scriptLabel', { defaultValue: 'Narration script' })}</span><span className="mg-hint-r">{t('media.scriptOptional', { defaultValue: 'Optional' })}</span></div>
                <textarea
                  value={reelScript}
                  onChange={(e) => setReelScript(e.target.value)}
                  maxLength={500}
                  placeholder={t('media.scriptPlaceholder', { defaultValue: 'Leave blank — AI writes it from your images and hook.' })}
                  aria-label={t('media.scriptLabel', { defaultValue: 'Narration script' })}
                />
                <div className="dv-script-help">{t('media.scriptHelp', { defaultValue: 'One or two sentences per image is plenty.' })}</div>
              </div>
            </div>
          ) : (
            <div className="dv-narr">
              <span className="dv-voicehint">{silentHint}</span>
            </div>
          )}
        </div>
        <div className="nvbk-div" />

        {/* Output row — aspect chip-group (single locked chip today) + optional
            describe-mode "4 scenes · auto" info chip + Publishes-to. */}
        <div className="mg-ctl">
          <div className="mg-ml"><span className="mg-t">{t('brandkit.aspect')}</span></div>
          <div className="dv-aspects">
            <span className="dv-aspectlock">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="11" width="12" height="9" rx="2" /><path d="M9 11V8a3 3 0 0 1 6 0v3" /></svg>
              9:16 · {t('media.vertical', { defaultValue: 'Vertical' })}
            </span>
            {isDescribeMode && (
              <span className="dv-aspectlock">{t('media.scenesAuto', { defaultValue: '4 scenes · auto' })}</span>
            )}
            <div className="dv-dest">
              <span className="dv-dest-lbl">{t('media.publishesTo', { defaultValue: 'Publishes to' })}</span>
              {/* All four take the same 9:16 vertical video — no "primary"
                  platform (owner 2026-07-09: singling out Instagram was misleading). */}
              <span className="dv-dest-chip">Instagram Reels</span>
              <span className="dv-dest-chip">TikTok</span>
              <span className="dv-dest-chip">Facebook</span>
              <span className="dv-dest-chip">Threads</span>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Destructive + irreversible — open the styled confirm dialog (not window.confirm).
  const remove = (asset) => setConfirmDel(asset);
  const confirmRemove = () => {
    const asset = confirmDel;
    setConfirmDel(null);
    if (!asset) return;
    mutate(async () => {
      setBusyId(asset.id);
      try {
        // TASK-334: an uploaded video never gets a brand_reference_assets row
        // (see visual-worker deleteBrandAsset vs deleteUploadedVideo), so
        // always calling brandAssets.remove() 404s every time for videos —
        // deleteBrandKitAsset dispatches to the matching endpoint by mimeType.
        await deleteBrandKitAsset(api, asset);
        setDetail((d) => (d && d.id === asset.id ? null : d));
        // TASK-324: the delete just released bytes from the STOCK
        // media_storage_bytes counter server-side — refresh so the header
        // meter shrinks without a poll.
        invalidateResource?.('entitlements');
      }
      finally { setBusyId(null); }
    });
  };

  // Video meter/CTA derived state (§5 A5) — video_reels_monthly, NEVER the
  // image allowance; CTA disabled reasons: photos-mode 0 scenes, describe-mode
  // <3 trimmed chars, single-flight video op running, quota at limit.
  const videoAllowance = entitlements?.usage?.video_reels_monthly || null;
  // Cinematic (video_cinematic_monthly) is metered SEPARATELY from standard
  // (video_reels_monthly). GA: cinematic is the DEFAULT quality when the plan
  // has it available; free/trial (0 quota) fall back to Slideshow + see upsell.
  const cinemaQuota = videoQuotaInfo(entitlements?.usage?.video_cinematic_monthly);
  const stdQuota = videoQuotaInfo(entitlements?.usage?.video_reels_monthly);
  // Cinematic quota only applies in DESCRIBE mode (photos mode always renders a
  // standard slideshow) — else out-of-cinematic dead-ends photos mode.
  const activeQuota = (isDescribeMode && reelQuality === 'cinematic') ? cinemaQuota : stdQuota;
  // At-limit = the ACTIVE quality has no room (over quota, or not on the plan).
  const videoAtLimit = activeQuota.known && !activeQuota.available;
  const videoCtaDisabled = isVideoGenerating || videoAtLimit
    || (isPhotosMode && pickedRefs.length === 0)
    || (isDescribeMode && genPrompt.trim().length < 3);

  return (
    <div className="cps-root">
      <MarketingSubnav />
      <div className="bk">
        {/* TASK-323: the top "Brand voice and visuals apply from Starter"
            banner is removed for posting — the logo-note (below, item 2 in
            the identity strip) and the compact locked generation row
            already carry that exact message, so this was a third repeat of
            it. The `postingWorkspace.brandKitBanner` key is kept (unused
            for now) in case a future plan wants this banner without also
            getting the two more specific notices below. */}

        {/* Identity strip — the logo slot AND the action button both open the
            dedicated "set your logo" upload (upload-as-logo + make-primary). */}
        <section className="card bk-identity">
          <button
            type="button"
            className={`bk-logo-slot ${primaryLogo ? '' : 'is-empty'}`}
            onClick={() => { if (brandingAllowed) logoFileRef.current?.click(); }}
            disabled={uploading || !brandingAllowed}
            title={brandingLocked ? t('planLock.tag') : primaryLogo ? t('brandkit.changeLogo') : t('brandkit.setLogo')}
          >
            {primaryLogo ? (
              <>
                <img src={primaryLogo.url} alt={t('brandkit.primaryLogo')} />
                <span className="bk-logo-star" title={t('brandkit.primaryLogo')}><Star size={11} /></span>
              </>
            ) : (
              <span className="bk-logo-empty">{brandingLocked ? <I.lock size={18} /> : <I.plus size={18} />}<span>{t('brandkit.setLogo')}</span></span>
            )}
          </button>
          <div className="bk-identity-info">
            <div className="bk-identity-nm">
              {brandName || t('brandkit.yourBrand')}
              {primaryLogo
                ? <span className="pill pill-ok"><span className="dot" />{t('brandkit.logoSet')}</span>
                : <span className="pill">{t('brandkit.noLogo')}</span>}
            </div>
            {brandColors.length > 0 && (
              <div className="bk-swatches">
                {brandColors.map((c, i) => <span key={i} className="bk-sw" style={{ background: c }} title={c} />)}
              </div>
            )}
            {/* TASK-323: the logo itself still saves and applies fine on a
                zero-visuals-allowance plan — only AI generation is locked —
                so this note explains where it becomes VISIBLE (on visuals)
                rather than blocking the upload above. Carries the single
                "View plans" link for this whole card (the locked generation
                row below has none of its own). */}
            {creationAllowed && imageGenerationLocked && (
              <div className="muted" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, fontSize: 12.5 }}>
                <span>{brandingLocked ? <><I.lock size={12} /> {t('brandkit.title')} · {t('planLock.tag')}</> : t('brandKitLock.logoNote')}</span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => openUpgrade({ feature: 'default' })}>
                  {t('brandKitLock.viewPlans')}
                </button>
              </div>
            )}
          </div>
          <div className="bk-identity-act">
            <button type="button" className="btn btn-ghost btn-sm bk-setlogo-btn" disabled={uploading || !brandingAllowed} onClick={() => { if (brandingAllowed) logoFileRef.current?.click(); }} title={brandingLocked ? t('planLock.tag') : undefined}>
              {brandingLocked ? <I.lock size={13} /> : <Star size={13} />} {primaryLogo ? t('brandkit.changeLogo') : t('brandkit.setLogo')}
            </button>
          </div>
          <input ref={logoFileRef} type="file" accept={PICK_ACCEPT} hidden onChange={onPickLogo} />
        </section>

        {typographyOn && (
          <section className="card bk-typo-section">
            <header className="bk-typo-head">
              <div><h2>{t('typography.title')}</h2><span className="bk-typo-beta">{brandingLocked ? <><I.lock size={12} /> {t('planLock.tag')}</> : t('typography.beta')}</span></div>
              <p>{t('typography.subtitle')}</p>
            </header>
            {typography?.defaultAssetId ? (
              <div className="bk-typo-filled">
                <div className="bk-typo-summary">
                  <div className="bk-typo-thumb" style={typographyDefaultAsset?.url ? { backgroundImage: `url("${typographyDefaultAsset.url}")` } : undefined}>
                    {!typographyDefaultAsset?.url && <span>Aa</span>}
                  </div>
                  <div className="bk-typo-name"><b>{typography?.font?.family || t('typography.mediaReference')}</b><span>{typography?.font?.source === 'catalog' ? t('typography.fromCatalog') : t('typography.fromMedia')}</span></div>
                  <div className="bk-typo-summary-actions">
                    <button type="button" className="btn btn-ghost btn-sm" disabled={!brandingAllowed} onClick={() => { if (brandingAllowed) setFontCatalogOpen(true); }}>{t('typography.swap')}</button>
                    <button type="button" className="btn btn-ghost btn-sm bk-typo-remove" disabled={!brandingAllowed} onClick={() => saveTypography(null)}>{t('typography.remove')}</button>
                  </div>
                </div>

                <div className="bk-typo-toggle-row">
                  <span><b>{t('typography.applyGenerated')}</b><small>{t('typography.applyHint')}</small></span>
                  <button type="button" className={`bk-typo-toggle ${typography.enabled ? 'is-on' : ''}`} role="switch" aria-checked={typography.enabled === true} disabled={!brandingAllowed} onClick={() => saveTypography({ ...typography, enabled: !typography.enabled, langsHash: typographyLangsHash })}><i /></button>
                </div>

                {/* Owner call 2026-08-02: the font IS the feature. Casing /
                    accent / placement / accent-color selects were speculative
                    prompt noise (a second color channel would fight the
                    brand-colors gate) — the reference specimen alone carries
                    the style. Backend tolerates the absent profile. */}
                <div className="bk-typo-honesty"><p>{t('typography.honestyRender')} {t('typography.honestyFallback')}</p></div>
              </div>
            ) : (
              <div className="bk-typo-empty">
                <span className="bk-typo-empty-icon">Aa</span>
                <div><b>{t('typography.emptyTitle')}</b><p>{t('typography.emptyBody')}</p></div>
                <div className="bk-typo-empty-actions">
                  <button type="button" className="btn btn-primary" disabled={!brandingAllowed} onClick={() => { if (brandingAllowed) setFontCatalogOpen(true); }}>{brandingLocked && <I.lock size={13} />} {t('typography.pickFont')}</button>
                  <button type="button" className="btn btn-ghost" disabled={!brandingAllowed || !markedAssetIds.length} title={!markedAssetIds.length ? t('typography.noMarkedAssets') : undefined} onClick={() => { if (brandingAllowed) setFontMediaPickerOpen(true); }}>{t('typography.chooseMedia')}</button>
                </div>
              </div>
            )}
            {fontError && <div className="bk-typo-error">{fontError}</div>}
          </section>
        )}

        {/* TASK-324 (#514): compact storage meter — "Storage · 1.2 GB of 2 GB"
            + a thin bar. Hidden entirely while usage is unknown
            (storageMeter.visible false — field absent, malformed, or an
            unlimited plan). Tone (warning >=80%, danger at 100%) only
            appears when storageMeter.enforced is true — i.e. the gateway's
            MEDIA_STORAGE_ENFORCED mirrors the visual-worker's
            STORAGE_ENFORCEMENT_ENABLED="1" for THIS env; while that flag is
            "0" the bar stays muted ('default' tone) even at/over the cap,
            because nothing is actually being blocked yet. The upload button
            below always stays clickable regardless of tone — whether an
            upload is actually rejected is decided server-side by that SAME
            enforcement flag, surfaced to the user via the real 402 (mapped
            to brandkit.storageFull in uploadFiles), not by this meter. */}
        {storageMeter.visible && (
          <div className={`bk-storage-meter bk-storage-meter--${storageMeter.tone}`}>
            <div className="bk-storage-meter-row">
              <span className="bk-storage-meter-l">{t('brandkit.storageLabel')}</span>
              <span className="bk-storage-meter-v">
                {t('brandkit.storageUsage', { used: storageMeter.usedLabel, limit: storageMeter.limitLabel })}
              </span>
            </div>
            <div className="pr"><div className="pr-fill" style={{ width: `${storageMeter.pct}%` }} /></div>
          </div>
        )}

        {/* Toolbar: tag filters + upload */}
        <div className="bk-bar">
          <div className="bk-filters">
            <button type="button" className={`bk-fchip ${activeTag === 'all' ? 'is-on is-all' : ''}`} aria-pressed={activeTag === 'all'} onClick={() => setActiveTag('all')}>
              <TagIcon tag="all" />{t('brandkit.tagAll')} <span className="mono bk-fcount">{assets.length}</span>
            </button>
            {TAGS.map((tag) => (
              <button key={tag} type="button" className={`bk-fchip ${activeTag === tag ? 'is-on' : ''}`} aria-pressed={activeTag === tag} onClick={() => setActiveTag(tag)}>
                <TagIcon tag={tag} />{t(`brandkit.tag_${tag}`)}
                {counts[tag] ? <span className="mono bk-fcount">{counts[tag]}</span> : null}
              </button>
            ))}
            {typographyOn && (
              <button type="button" className={`bk-fchip ${activeTag === 'font' ? 'is-on' : ''}`} aria-pressed={activeTag === 'font'} onClick={() => setActiveTag('font')}>
                <TagIcon tag="font" />{t('typography.fontFilter')}
                {markedAssetIds.length ? <span className="mono bk-fcount">{markedAssetIds.length}</span> : null}
              </button>
            )}
            {/* §6 B1 — Videos filter chip; same chip component as the tag chips.
                Decoupled from showReel (2026-07): uploaded videos must be
                filterable even with reel GENERATION off — the chip now keys
                purely on "is there a video asset", generated OR uploaded. */}
            {videoCount > 0 && (
              <button type="button" className={`bk-fchip ${activeTag === 'videos' ? 'is-on' : ''}`} aria-pressed={activeTag === 'videos'} onClick={() => setActiveTag('videos')}>
                <TagIcon tag="videos" />{t('media.filterVideos', { defaultValue: 'Videos' })}
                <span className="mono bk-fcount">{videoCount}</span>
              </button>
            )}
            {/* TASK-325 — quiet chip: a separate axis from the 5 brand tags
                above, so it lives out here rather than in the TAGS.map loop.
                Only appears once there is at least one auto-saved post
                visual, same "only show when relevant" rule as Videos. */}
            {postOriginCount > 0 && (
              <button type="button" className={`bk-fchip ${activeTag === 'posts' ? 'is-on' : ''}`} aria-pressed={activeTag === 'posts'} onClick={() => setActiveTag('posts')}>
                <TagIcon tag="posts" />{t('brandkit.filterFromPosts')}
                <span className="mono bk-fcount">{postOriginCount}</span>
              </button>
            )}
          </div>
          {/* Upload button removed — the "Add references" dropzone below does the
              same; the hidden input it triggers stays here. */}
          <input ref={fileRef} type="file" accept={PICK_ACCEPT} multiple hidden onChange={onPick} />
          <input ref={videoFileRef} type="file" accept={VIDEO_ACCEPT} hidden onChange={onPickVideo} />
        </div>

        {/* Generate panel — make an on-brand image straight into the library.
            TASK-323: when the visuals allowance is confirmed zero, the
            trigger card is REPLACED by a compact locked row (same card
            chrome, sparkle icon + a single line — no prompt input, no
            meter, no Generate button) instead of showing the normal
            trigger disabled-but-fully-worded, which read as "usable" even
            though nothing behind it worked. The "View plans" link lives
            only on the logo note above — one per screen. */}
        {!creationAllowed ? <CreationAccessNotice /> : <div className={`bk-genlayout ${genOpen ? 'is-open' : ''}`}>
          {(!genOpen || brandingLocked) && (
            imageGenerationLocked ? (
              <div className="card bk-gentrigger bk-gentrigger-locked" role="status">
                <span className="bk-gentrigger-ic"><I.lock size={18} /></span>
                <span className="bk-gentrigger-tx">
                  <span className="bk-gentrigger-t">{t('brandKitLock.genTitle')}</span>
                </span>
              </div>
            ) : (
              <button type="button" className="card bk-gentrigger" onClick={openGen}>
                <span className="bk-gentrigger-ic"><I.sparkles size={18} /></span>
                <span className="bk-gentrigger-tx">
                  <span className="bk-gentrigger-t">{t('brandkit.genDescribe')}</span>
                  <span className="bk-gentrigger-s">{t('brandkit.genTriggerSub')}</span>
                </span>
                {allowance && allowance.limit != null && (
                  <span className="bk-gentrigger-meter">{t('brandkit.imagesRemaining', { count: allowance.remaining ?? 0, limit: allowance.limit })}</span>
                )}
                <span className="bk-gentrigger-cta"><I.plus size={15} /> {t('brandkit.generate')}</span>
              </button>
            )
          )}
          {genOpen && !brandingLocked && (<>
          <section className="card bk-gen">
            <div className="bk-gen-l">
              {/* A0 — panel header, kind-aware: play glyph + "Create a video" in
                  Video mode (video-forms-v2-refresh-design-brief.md §5 A0). */}
              <span className="bk-gen-t">
                {isReel
                  ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg> {t('media.genVideoTitle', { defaultValue: 'Create a video' })}</>
                  : <><I.sparkles size={14} /> {t('brandkit.genDescribe')}</>}
              </span>
              <button type="button" className="bk-gen-close" onClick={closeGen} aria-label="Close"><I.x size={15} /></button>
            </div>
            {/* Media-kind toggle — ADMIN-ONLY dark video pilot (showReel gates it).
                Reuses the .bk-aspect segmented recipe (see video.css .dv-kind). */}
            {showReel && (
              <div className="dv-kind" role="radiogroup" aria-label={t('media.mediaKind', { defaultValue: 'Media type' })}>
                <button type="button" role="radio" aria-checked={genKind === 'image'} className={genKind === 'image' ? 'on' : ''} onClick={() => setGenKind('image')}>
                  <I.image size={13} /> {t('media.kindImage', { defaultValue: 'Image' })}
                </button>
                <button type="button" role="radio" aria-checked={genKind === 'video'} className={genKind === 'video' ? 'on' : ''} onClick={() => setGenKind('video')}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg> {t('media.kindVideo', { defaultValue: 'Video' })}
                </button>
              </div>
            )}

            {isReel ? (<>
              {/* NEW .dv-src source seg (lite-first addendum §2) — Describe it |
                  My photos, video-kind only, default Describe it. */}
              <div className="dv-src" role="radiogroup" aria-label={t('media.mediaSource', { defaultValue: 'Video source' })}>
                <button type="button" role="radio" aria-checked={srcMode === 'describe'} className={srcMode === 'describe' ? 'on' : ''} onClick={() => setSrcMode('describe')}>
                  {t('media.srcDescribe', { defaultValue: 'Describe it' })}
                </button>
                <button type="button" role="radio" aria-checked={srcMode === 'photos'} className={srcMode === 'photos' ? 'on' : ''} onClick={() => setSrcMode('photos')}>
                  {t('media.srcMyPhotos', { defaultValue: 'My photos' })}
                </button>
              </div>

              {isDescribeMode ? (
                <>
                  {/* Quality — Tier-2 cinematic (fal.ai/Kling) vs Standard slideshow.
                      Admin pilot + dark client flag; VISIBLE (not buried in Options)
                      so it's a clear, primary choice. Server double-gates. */}
                  {videoCinematicEnabled() && (
                    <div className="mg-ctl">
                      <div className="mg-ml"><span className="mg-t">{t('media.quality', { defaultValue: 'Quality' })}</span></div>
                      <div className="bk-chips">
                        <button type="button" className={`bk-chip ${reelQuality === 'cinematic' ? 'on' : ''}`} aria-pressed={reelQuality === 'cinematic'} onClick={() => { reelQualityTouchedRef.current = true; setReelQuality('cinematic'); }}>{t('media.qualityCinematic', { defaultValue: 'Cinematic' })}</button>
                        <button type="button" className={`bk-chip ${reelQuality === 'standard' ? 'on' : ''}`} aria-pressed={reelQuality === 'standard'} onClick={() => { reelQualityTouchedRef.current = true; setReelQuality('standard'); }}>{t('media.qualityStandard', { defaultValue: 'Slideshow' })}</button>
                      </div>
                      <span className="dv-voicehint">{reelQuality === 'cinematic' ? t('media.qualityCinematicHint', { defaultValue: 'Cinematic motion — richer movement, takes a little longer to render.' }) : t('media.qualityStandardHint2', { defaultValue: 'Quick narrated video built from your images.' })}</span>
                      {/* Separate monthly quotas — cinematic + standard reels. */}
                      <span className="dv-quota">
                        <span className="dv-quota-lbl">{t('media.quotaLeftLabel', { defaultValue: 'Left this month:' })}</span>
                        <b className={reelQuality === 'cinematic' ? 'is-on' : ''}>{t('media.qualityCinematic', { defaultValue: 'Cinematic' })} {cinemaQuota.known ? (cinemaQuota.unlimited ? '∞' : `${Math.max(0, cinemaQuota.remaining)} of ${cinemaQuota.limit ?? 0}`) : '…'}</b>
                        <span className="dv-quota-sep">·</span>
                        <b className={reelQuality === 'standard' ? 'is-on' : ''}>{t('media.qualityStandard', { defaultValue: 'Slideshow' })} {stdQuota.known ? (stdQuota.unlimited ? '∞' : `${Math.max(0, stdQuota.remaining)} of ${stdQuota.limit ?? 0}`) : '…'}</b>
                        {reelQuality === 'cinematic' && cinemaQuota.known && !cinemaQuota.available && (
                          <Link to="/app/plan" className="dv-quota-up">{t('media.upgradeShort', { defaultValue: 'Upgrade' })} <I.arrowRight size={10} /></Link>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="bk-prompt">
                    <textarea
                      ref={promptRef}
                      value={genPrompt}
                      onChange={(e) => setGenPrompt(e.target.value)}
                      placeholder={t('media.describePlaceholder', { defaultValue: "What is the video about? e.g. 'Three tips for a stress-free office move'" })}
                      onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !generating) doGenerate(); }}
                      maxLength={500}
                    />
                  </div>
                  <div className="mg-refhelp">{t('media.describeHint', { defaultValue: 'AI writes the story, creates four scenes and narrates it — usually 20–30 seconds.' })}</div>

                  {/* Brand-voice toggle — the LIVE .nvbk-ms shield reused verbatim,
                      copy-only variant (media.brandVoice/bvOn/bvOff). Describe mode
                      ONLY — a dead control (and absent) in Photos mode (§1 LAW). */}
                  <div className={`nvbk-ms dv-bv ${genBrandVoice ? '' : 'is-off'}`}>
                    <span className="nvbk-ms-ic"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" />{genBrandVoice ? <path d="m9 12 2 2 4-4" /> : <path d="M4 4l16 16" />}</svg></span>
                    <span className="nvbk-ms-txt"><span className="nvbk-ms-n">{t('media.brandVoice', { defaultValue: 'Brand voice' })}</span><span className="nvbk-ms-h">{genBrandVoice ? t('media.bvOn', { defaultValue: 'Uses your brand voice & content plan' }) : t('media.bvOff', { defaultValue: 'Clean creation — no brand context' })}</span></span>
                    <button type="button" role="switch" aria-checked={genBrandVoice} aria-label={t('media.brandVoice', { defaultValue: 'Brand voice' })} className={`nvbk-ms-toggle ${genBrandVoice ? 'on' : 'off'}`} onClick={() => setGenBrandVoice((v) => !v)}>
                      <span className="nvbk-ms-rail on-rail">{t('createPost.toggleOn')}</span>
                      <span className="nvbk-ms-rail off-rail">{t('createPost.toggleOff')}</span>
                      <span className="nvbk-ms-knob">
                        <span className="nvbk-shield-on"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /><path d="m9 12 2 2 4-4" /></svg></span>
                        <span className="nvbk-shield-off"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /><path d="M4 4l16 16" /></svg></span>
                      </span>
                    </button>
                  </div>

                  {/* Optional reference photos ("1 click with a reference") —
                      describe mode ONLY, quiet compact row, never a required
                      step. The AI-generated scenes will feature the subject. */}
                  <div className="nvbk-div" />
                  {renderDescribeRefRow()}
                </>
              ) : (
                /* Photos mode — the scenes rail EXACTLY as designed, primary
                   input here, NOT inside Options. */
                renderScenesRow()
              )}

              {/* Options disclosure (.dv-opts) — collapsed by default, both
                  source modes; hook / narration / output row live inside. */}
              <div className="nvbk-div" />
              <button type="button" className="dv-opts-trigger" onClick={() => setVideoOptsOpen((v) => !v)} aria-expanded={videoOptsOpen}>
                <span>{t('media.videoOptions', { defaultValue: 'Options' })}</span>
                <svg className="dv-opts-cv" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
              </button>
              {videoOptsOpen && (
                <div className="dv-opts">
                  {renderVideoOpts()}
                </div>
              )}
            </>) : (<>
            <div className="bk-prompt">
              <textarea
                ref={promptRef}
                value={genPrompt}
                onChange={(e) => setGenPrompt(e.target.value)}
                placeholder={t('brandkit.genPlaceholder')}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !generating) doGenerate(); }}
                maxLength={500}
              />
            </div>

            {/* Brand GATEWAY — the SAME shield component as New Post, so the
                brand on/off decision reads identically across the app. Shown
                only when a brand profile exists; without one, the custom
                art-direction panel below is the default. */}
            {brandProfile && (
              <div className={`nvbk-ms ${genBrandAware ? '' : 'is-off'}`} style={{ marginTop: 4 }}>
                <span className="nvbk-ms-ic"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" />{genBrandAware ? <path d="m9 12 2 2 4-4" /> : <path d="M4 4l16 16" />}</svg></span>
                <span className="nvbk-ms-txt"><span className="nvbk-ms-n">{t('brandkit.gateway')}</span><span className="nvbk-ms-h">{t('brandkit.gatewaySub')}</span></span>
                <button type="button" role="switch" aria-checked={genBrandAware} aria-label={t('brandkit.gateway')} className={`nvbk-ms-toggle ${genBrandAware ? 'on' : 'off'}`} onClick={() => setGenBrandAware((v) => !v)}>
                  <span className="nvbk-ms-rail on-rail">{t('createPost.toggleOn')}</span>
                  <span className="nvbk-ms-rail off-rail">{t('createPost.toggleOff')}</span>
                  <span className="nvbk-ms-knob">
                    <span className="nvbk-shield-on"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /><path d="m9 12 2 2 4-4" /></svg></span>
                    <span className="nvbk-shield-off"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 4 7v5c0 4.5 3.2 7.8 8 9 4.8-1.2 8-4.5 8-9V7z" /><path d="M4 4l16 16" /></svg></span>
                  </span>
                </button>
              </div>
            )}

            {(brandProfile && genBrandAware) ? (
              /* ON-brand — light controls grouped exactly like New Post's brand group. */
              <div className="nvbk-brandgroup">
                <div className="nvbk-bg-cap"><span className="nvbk-bg-t">{t('brandkit.brandShapes')}</span></div>
                <div className="nvbk-bg-inner">
                  <div className="mg-ctl">
                    <div className="mg-ml"><span className="mg-t">{t('brandkit.style')}</span></div>
                    <div className="bk-chips">
                      {STYLE_CUSTOM.map((s) => (
                        <button key={s} type="button" className={`bk-chip ${genStyle === s ? 'on' : ''}`} onClick={() => setGenStyle(s)}>{t(`brandkit.style_${s}`)}</button>
                      ))}
                    </div>
                  </div>
                  <div className="nvbk-div" />
                  {renderRefRow()}
                  <div className="nvbk-div" />
                  {renderAspectRow()}
                  <div className="nvbk-div" />
                  <div className="mg-confirm">
                    {brandColors.length > 0 && <span className="mg-sw">{brandColors.map((c, i) => <i key={i} style={{ background: c }} />)}</span>}
                    <span className="mg-confirm-t">{t('brandkit.usesBrand')}</span>
                    <Link to="/app/plan" className="mg-confirm-a">{t('brandkit.editStrategy')} <I.arrowRight size={11} /></Link>
                  </div>
                </div>
              </div>
            ) : (
              /* Brand-free — expanded art-direction panel (gold accent). Every
                 control folds into the prompt client-side; all optional. */
              <div className="nvbk-brandgroup" style={{ '--accent': '#D4A53F', '--accent-soft': 'color-mix(in srgb, #D4A53F 16%, transparent)', '--accent-text': '#C08A1E' }}>
                <div className="nvbk-bg-cap"><span className="nvbk-bg-t">{t('brandkit.artDirection')}</span><span className="nvbk-bg-sp" /><span className="mg-nofree">{t('brandkit.noTextLogos')}</span></div>
                <div className="nvbk-bg-inner">
                  <div className="mg-ctl">
                    <div className="mg-ml"><span className="mg-t">{t('brandkit.style')}</span></div>
                    <div className="bk-chips">
                      {STYLE_CUSTOM.map((s) => (
                        <button key={s} type="button" className={`bk-chip ${genStyle === s ? 'on' : ''}`} onClick={() => setGenStyle(s)}>{t(`brandkit.style_${s}`)}</button>
                      ))}
                    </div>
                  </div>
                  <div className="nvbk-div" />
                  {renderRefRow()}
                  <div className="nvbk-div" />
                  {/* Aspect (image) — essential, stays visible; reel mode swaps
                      in the 9:16 lock + narration sub-panel in the same slot. */}
                  {renderAspectRow()}
                  {/* Advanced art direction — progressive disclosure. Mood /
                      Lighting / Composition / custom Palette are all optional and
                      secondary, so they hide behind a toggle to keep the panel calm. */}
                  <div className="nvbk-div" />
                  <button type="button" className={`mg-more ${genAdvancedOpen ? 'is-open' : ''}`} onClick={() => setGenAdvancedOpen((v) => !v)} aria-expanded={genAdvancedOpen}>
                    <span>{genAdvancedOpen ? t('brandkit.fewerOptions') : t('brandkit.moreOptions')}</span>
                    <svg className="mg-more-cv" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
                  </button>
                  {genAdvancedOpen && (
                    <>
                      <div className="nvbk-div" />
                      <div className="mg-ctl">
                        <div className="mg-ml"><span className="mg-t">{t('brandkit.mood')}</span></div>
                        <div className="bk-chips">
                          {MOOD_KEYS.map((m) => (
                            <button key={m} type="button" className={`bk-chip ${genMood === m ? 'on' : ''}`} onClick={() => setGenMood((v) => (v === m ? '' : m))}>{t(`brandkit.mood_${m}`)}</button>
                          ))}
                        </div>
                      </div>
                      <div className="nvbk-div" />
                      <div className="mg-ctl">
                        <div className="mg-ml"><span className="mg-t">{t('brandkit.lighting')}</span></div>
                        <div className="bk-chips">
                          {LIGHT_KEYS.map((m) => (
                            <button key={m} type="button" className={`bk-chip ${genLighting === m ? 'on' : ''}`} onClick={() => setGenLighting((v) => (v === m ? '' : m))}>{t(`brandkit.light_${m}`)}</button>
                          ))}
                        </div>
                      </div>
                      <div className="nvbk-div" />
                      <div className="mg-ctl">
                        <div className="mg-ml"><span className="mg-t">{t('brandkit.composition')}</span></div>
                        <div className="bk-chips">
                          {COMP_KEYS.map((m) => (
                            <button key={m} type="button" className={`bk-chip ${genComposition === m ? 'on' : ''}`} onClick={() => setGenComposition((v) => (v === m ? '' : m))}>{t(`brandkit.comp_${m}`)}</button>
                          ))}
                        </div>
                      </div>
                      <div className="nvbk-div" />
                      <div className="mg-ctl">
                        <div className="mg-ml"><span className="mg-t">{t('brandkit.palette')}</span></div>
                        <div className="mg-pal">
                          {genPalette.map((c, i) => (
                            // Each swatch IS an in-place colour input: click to edit that
                            // one (drag updates only this index — no append-on-drag bug).
                            <label key={i} className="mg-ps" style={{ background: c }} title={c}>
                              <input type="color" value={c} aria-label={t('brandkit.editColor')} onChange={(e) => { const v = e.target.value; setGenPalette((p) => p.map((x, j) => (j === i ? v : x))); }} />
                              <button type="button" className="mg-ps-x" onClick={(e) => { e.preventDefault(); setGenPalette((p) => p.filter((_, j) => j !== i)); }} aria-label={t('brandkit.removeColor')}>×</button>
                            </label>
                          ))}
                          {genPalette.length < 6 && (
                            <button type="button" className="mg-pal-add" onClick={() => setGenPalette((p) => (p.length < 6 ? [...p, '#888888'] : p))} aria-label={t('brandkit.addColor')}>+</button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            </>)}

            {/* A5 footer — video mode: needs-frames / needs-describe notice + the
                video quota meter (video_reels_monthly, NEVER the image allowance)
                + video CTA. Image mode: byte-identical to today. */}
            {isReel ? (
              <>
                {isPhotosMode && pickedRefs.length === 0 && (
                  <div className="bk-notice is-error">
                    <span>{t('media.videoNeedsFrames', { defaultValue: 'Pick at least one image to build the video from.' })}</span>
                  </div>
                )}
                {isDescribeMode && genPrompt.trim().length < 3 && (
                  <div className="bk-notice is-error">
                    <span>{t('media.describeMin', { defaultValue: 'Describe the video in a few words first.' })}</span>
                  </div>
                )}
                {isVideoGenerating && (
                  <div className="bk-notice">
                    <span>{t('media.oneRendering', { defaultValue: 'A video is already rendering — one at a time.' })}</span>
                  </div>
                )}
                <div className="bk-gen-foot">
                  {videoAllowance && (
                    <span className={`bk-meter ${(videoAllowance.limit != null && (videoAllowance.remaining ?? 0) <= 0) ? 'is-limit' : ''}`}>
                      {videoAllowance.limit != null && (
                        <span className="bk-meter-bar"><i style={{ width: `${Math.min(100, Math.round(((videoAllowance.used ?? 0) / Math.max(1, videoAllowance.limit)) * 100))}%` }} /></span>
                      )}
                      {videoAllowance.limit == null ? t('settings.unlimited') : t('media.videoQuota', { defaultValue: `${videoAllowance.used ?? 0}/${videoAllowance.limit} videos this month`, used: videoAllowance.used ?? 0, limit: videoAllowance.limit })}
                    </span>
                  )}
                  <button type="button" className="btn btn-primary bk-cta" disabled={videoCtaDisabled} onClick={doGenerate}>
                    {isVideoGenerating ? <span className="bk-gspin" aria-hidden="true" /> : <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>}
                    {isVideoGenerating ? t('media.renderingVideo', { defaultValue: 'Generating video…' }) : t('media.generateVideo', { defaultValue: 'Generate video' })}
                  </button>
                </div>
                {videoAtLimit && (
                  <div className="bk-notice is-error">
                    <span>{t('media.videoLimit', { defaultValue: 'Monthly video limit reached.' })}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="bk-gen-foot">
                {allowance && (
                  <span className="bk-meter">
                    {allowance.limit != null && (
                      <span className="bk-meter-bar"><i style={{ width: `${Math.min(100, Math.round(((allowance.used ?? 0) / Math.max(1, allowance.limit)) * 100))}%` }} /></span>
                    )}
                    {allowance.limit == null ? t('settings.unlimited') : t('brandkit.imagesRemaining', { count: allowance.remaining ?? 0, limit: allowance.limit })}
                  </span>
                )}
                <button type="button" className="btn btn-primary bk-cta" disabled={generating || imageGenerationLocked} onClick={doGenerate}>
                  <I.sparkles size={15} /> {generating ? t('brandkit.generating') : t('brandkit.generate')}
                </button>
                {imageGenerationLocked && <span className="muted" role="status">{t('imageLocked.lockedLabel')} {t('imageLocked.uploadHint')}</span>}
              </div>
            )}
          </section>

          <aside className="card bk-genside">
            {isReel ? (<>
              <span className="bk-eyebrow">{t('brandkit.genWhatHappens')}</span>
              <div className="bk-genside-t">{t('media.videoFlowIntro', { defaultValue: "Here's how your video comes together." })}</div>
              <div className="bk-flow">
                {/* Mode-aware: the first step differs for photos vs describe. Copy
                    is resolved inline (label + desc) — the old code indexed the
                    description object with the LABEL key ([l] not [d]), so every
                    description fell through to the raw i18n key on screen. */}
                {(srcMode === 'photos'
                  ? [
                      ['image', t('media.videoStepPhoto1', { defaultValue: 'Pick 1–8 images' }), t('media.videoStepPhoto1d', { defaultValue: 'They become the scenes, in order.' })],
                      ['sparkles', t('media.videoStep2', { defaultValue: 'AI writes the hook & narration' }), t('media.videoStep2d', { defaultValue: 'Or type your own hook and script.' })],
                      ['check', t('media.videoStep3', { defaultValue: 'Rendered in minutes' }), t('media.videoStep3d', { defaultValue: 'The finished video lands in your library.' })],
                    ]
                  : [
                      ['image', t('media.videoStepDesc1', { defaultValue: 'Describe your video' }), t('media.videoStepDesc1d', { defaultValue: 'AI plans the scenes and generates the visuals.' })],
                      ['sparkles', t('media.videoStep2', { defaultValue: 'AI writes the hook & narration' }), t('media.videoStep2d', { defaultValue: 'Or type your own hook and script.' })],
                      ['check', t('media.videoStep3', { defaultValue: 'Rendered in minutes' }), t('media.videoStep3d', { defaultValue: 'The finished video lands in your library.' })],
                    ]
                ).map(([ic, l, d], i) => (
                  <div key={i} className="bk-fl">
                    <span className="bk-fl-ic">{I[ic] ? I[ic]({ size: 12 }) : null}</span>
                    <div>
                      <div className="bk-fl-l">{l}</div>
                      <div className="bk-fl-d">{d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>) : (<>
              <span className="bk-eyebrow">{t('brandkit.genWhatHappens')}</span>
              <div className="bk-genside-t">{t('brandkit.genJoinsLibrary')}</div>
              <div className="bk-flow">
                {[['sparkles', 'genStep1', 'genStep1d'], ['check', 'genStep2', 'genStep2d'], ['image', 'genStep3', 'genStep3d']].map(([ic, l, d]) => (
                  <div key={l} className="bk-fl">
                    <span className="bk-fl-ic">{I[ic] ? I[ic]({ size: 12 }) : null}</span>
                    <div><div className="bk-fl-l">{t(`brandkit.${l}`)}</div><div className="bk-fl-d">{t(`brandkit.${d}`)}</div></div>
                  </div>
                ))}
              </div>
            </>)}
          </aside>
          </>)}
        </div>}

        {notice && (
          <div className={`bk-notice is-${notice.kind}`}>
            <span>{notice.text}</span>
            {notice.action && (
              <span className="bk-notice-a"><button type="button" className="btn btn-ghost btn-sm" onClick={notice.action.onClick}>{notice.action.label}</button></span>
            )}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="bk-grid"><div className="bk-skel" /><div className="bk-skel" /><div className="bk-skel" /><div className="bk-skel" /></div>
        ) : (
          <div className="bk-grid">
            <button type="button"
              className={`bk-dz ${dragOver ? 'is-drag' : ''}`}
              disabled={!creationAllowed}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <span className="bk-dz-ic"><MediaUploadIcon /></span>
              <span className="bk-dz-t">{t('brandkit.addRefs')}</span>
              <span className="bk-dz-s">{t('brandkit.dzHint')}</span>
            </button>

            {/* "Upload your own video" — separate tile from the image dropzone
                above (different accept type, size/duration validation, and a
                poster-capture step). Always visible, independent of showReel/
                reel generation — this only posts an already-made file. */}
            <button
              type="button"
              className={`bk-dz bk-dz-video ${uploadingVideo ? 'is-busy' : ''}`}
              onClick={() => { if (!uploadingVideo) videoFileRef.current?.click(); }}
              disabled={uploadingVideo || !creationAllowed}
            >
              <span className="bk-dz-ic">
                {uploadingVideo
                  ? <span className="bk-gspin" aria-hidden="true" />
                  : <MediaUploadIcon video />}
              </span>
              <span className="bk-dz-t">{uploadingVideo ? t('media.uploadingVideo', { defaultValue: 'Uploading…' }) : t('media.uploadVideo', { defaultValue: 'Upload video' })}</span>
              <span className="bk-dz-s">{t('media.uploadVideoHint', { defaultValue: 'MP4 or MOV, up to 100 MB' })}</span>
            </button>

            {genTiles.map((g) => (
              <div key={g.id} className={`bk-asset bk-asset-gen ${g.kind === 'reel' ? 'is-video' : ''}`}>
                <div className="bk-thumb bk-gen-thumb">
                  <div className="bk-shimmer" aria-hidden="true" />
                  <div className="bk-gline">
                    <span className="bk-gspin" aria-hidden="true" />
                    <span>{g.kind === 'reel' ? t('media.renderingVideo', { defaultValue: 'Generating video…' }) : t('brandkit.generating')}</span>
                    {g.kind === 'reel' && <ElapsedEta startedAt={g.startedAt} />}
                  </div>
                  {g.kind === 'reel' && <span className="dv-badge dv-badge-kind">{t('media.badgeVideo', { defaultValue: 'VIDEO' })}</span>}
                </div>
                {g.prompt && <span className="bk-tagchip bk-gen-echo">{g.prompt}</span>}
              </div>
            ))}

            {visible.map((a) => (
              <div
                key={a.id}
                className={`bk-asset ${a.id === primaryLogoId ? 'is-logo' : ''} ${(a.mimeType || '').startsWith('video/') ? 'is-video' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setDetail(a)}
                onKeyDown={(e) => { if (e.key === 'Enter') setDetail(a); }}
              >
                {a.id === primaryLogoId && <span className="bk-star-badge" title={t('brandkit.primaryLogo')}><Star size={12} /></span>}
                <div className="bk-thumb" style={(() => { const bg = (a.mimeType || '').startsWith('video/') ? a.posterUrl : a.url; return bg ? { backgroundImage: `url("${bg}")` } : undefined; })()}>
                  {(a.mimeType || '').startsWith('video/') && !a.posterUrl && a.url && <VideoFramePreview url={a.url} />}
                  {typographyOn && markedAssetSet.has(a.id) && <span className="chip c-font">Aa {t('typography.fontRef')}</span>}
                  {typographyOn && typography?.defaultAssetId === a.id && <span className="bk-typo-default-badge">{t('typography.defaultBadge')}</span>}
                  {!(a.mimeType || '').startsWith('video/') && !a.url && <span className="bk-thumb-ph">{a.fileName || a.tag}</span>}
                  {(a.mimeType || '').startsWith('video/') && (
                    <>
                      <span className="dv-play" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></span>
                      <span className="dv-badge dv-badge-kind">{t('media.badgeVideo', { defaultValue: 'VIDEO' })}</span>
                      {formatDuration(a.durationSeconds) && <span className="dv-badge dv-badge-dur">{formatDuration(a.durationSeconds)}</span>}
                      <span className="bk-video-name" title={a.fileName || t('media.videoLabel', { defaultValue: 'Video' })}>{a.fileName || t('media.videoLabel', { defaultValue: 'Video' })}</span>
                    </>
                  )}
                  {a.referencesDropped && (
                    <span className="bk-refdrop" title={t('brandkit.refsDroppedHint')}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3l18 18" /><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /></svg>
                      {t('brandkit.refsDroppedBadge')}
                    </span>
                  )}
                </div>
                {/* Image-only affordances (set-as-logo, tag chip, per-tile delete)
                    operate on brand_reference_assets; a GENERATED reel is a
                    media_assets row with no owner-delete UI (view+play only).
                    An UPLOADED video (provider 'upload') IS user-owned media —
                    it gets the same delete affordance as images, just without
                    the set-as-logo star or tag chip (neither concept applies). */}
                {!(a.mimeType || '').startsWith('video/') ? (
                  <>
                    {typographyOn ? (
                      <div className="bk-typo-card-menu">
                        <button
                          type="button"
                          className="bk-typo-kebab"
                          aria-label={t('typography.assetMenu')}
                          aria-haspopup="true"
                          aria-expanded={assetMenuId === a.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            // TASK-326: stash the trigger so useDismissableMenu can
                            // exempt it from the outside-pointerdown close and
                            // return focus here when the menu closes.
                            assetMenuTriggerRef.current = event.currentTarget;
                            setAssetMenuId((id) => id === a.id ? null : a.id);
                          }}
                        ><I.dots size={15} /></button>
                        {assetMenuId === a.id && (
                          // Fix list item 13: this is a plain button group, not a
                          // real menu widget (no roving tabindex/arrow-key nav is
                          // implemented), so it no longer claims role="menu" /
                          // "menuitem" — role="group" + aria-label describes what
                          // it actually is.
                          <div ref={assetMenuRef} className="bk-typo-menu-pop" role="group" aria-label={t('typography.assetMenu')} onClick={(event) => event.stopPropagation()}>
                            <button type="button" disabled={!brandingAllowed || typography?.defaultAssetId === a.id} onClick={() => { setTypographyDefault(a); closeAssetMenu(); }}>{t('typography.setDefault')}</button>
                            <button type="button" disabled={!brandingAllowed || !markedAssetSet.has(a.id)} onClick={() => { removeTypographyReference(a); closeAssetMenu(); }}>{t('typography.removeReference')}</button>
                            <span />
                            {/* TASK-325 fix (major #3): a post-origin tile (tag='post')
                                can't be retagged or set as the primary logo (server
                                400s POST_ASSET_NOT_RETAGGABLE) — hide the action
                                instead of surfacing a rejection. */}
                            {a.tag !== 'post' && (
                              <button type="button" disabled={!brandingAllowed || busyId === a.id || a.id === primaryLogoId} onClick={() => { setAsLogo(a); closeAssetMenu(); }}>{brandingLocked && <I.lock size={12} />} {a.id === primaryLogoId ? t('brandkit.primaryLogo') : t('brandkit.makeLogo')}</button>
                            )}
                            <button type="button" className="is-danger" disabled={busyId === a.id} onClick={() => { remove(a); closeAssetMenu(); }}>{t('brandkit.delete')}</button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bk-asset-acts">
                        {/* TASK-325 fix (major #3): same guard as the typography
                            menu above — a post-origin tile can't be made the
                            primary logo. */}
                        {a.tag !== 'post' && (
                          <button
                            type="button"
                            className={`bk-act-star ${a.id === primaryLogoId ? 'is-on' : ''}`}
                            title={brandingLocked ? t('planLock.tag') : a.id === primaryLogoId ? t('brandkit.primaryLogo') : t('brandkit.makeLogo')}
                            disabled={!brandingAllowed || busyId === a.id || a.id === primaryLogoId}
                            onClick={(e) => { e.stopPropagation(); setAsLogo(a); }}
                          >
                            {brandingLocked ? <I.lock size={13} /> : <Star size={13} filled={a.id === primaryLogoId} />}
                          </button>
                        )}
                        <button
                          type="button"
                          className="bk-act-del"
                          title={t('brandkit.delete')}
                          disabled={busyId === a.id}
                          onClick={(e) => { e.stopPropagation(); remove(a); }}
                        >
                          <I.trash size={13} />
                        </button>
                      </div>
                    )}
                    <span className="bk-tagchip"><TagIcon tag={a.tag} /><span className="bk-tag-label">{t(a.tag === 'post' ? 'brandkit.filterFromPosts' : `brandkit.tag_${a.tag}`)}</span></span>
                  </>
                ) : a.provider === 'upload' && (
                  <div className="bk-asset-acts">
                    <button
                      type="button"
                      className="bk-act-del"
                      title={t('brandkit.delete')}
                      disabled={busyId === a.id}
                      onClick={(e) => { e.stopPropagation(); remove(a); }}
                    >
                      <I.trash size={13} />
                    </button>
                  </div>
                )}
              </div>
            ))}

            {visible.length === 0 && (
              <div className="bk-empty-cell">{t('brandkit.emptyTag')}</div>
            )}
          </div>
        )}
      </div>

      {/* Asset detail modal */}
      {detail && (
        <div className="bk-modal-bg" onClick={() => setDetail(null)}>
          <div className="bk-modal card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <button type="button" className="bk-modal-x" onClick={() => setDetail(null)} aria-label={t('brandkit.close')}><I.x size={16} /></button>
            {(detail.mimeType || '').startsWith('video/') ? (
              <>
                <div className="bk-modal-preview">
                  {detail.url
                    ? <video className="dv-player" src={detail.url} poster={detail.posterUrl || undefined} controls playsInline autoPlay preload="auto" />
                    : <span className="bk-thumb-ph">{t('brandkit.untitled')}</span>}
                </div>
                <div className="bk-modal-body">
                  <div className="bk-modal-fn">
                    {detail.fileName || t('media.videoLabel', { defaultValue: 'Video' })}
                    {formatDuration(detail.durationSeconds) ? ` · ${formatDuration(detail.durationSeconds)}` : ''}
                  </div>
                  {/* Only user-uploaded videos are owner-deletable here — a
                      generated reel stays view-only, same rule as the grid tile. */}
                  {detail.provider === 'upload' && (
                    <button type="button" className="bk-modal-del" disabled={busyId === detail.id} onClick={() => remove(detail)}>
                      <I.trash size={13} /> {t('brandkit.delete')}
                    </button>
                  )}
                </div>
              </>
            ) : (
            <>
            <div className="bk-modal-preview" style={detail.url ? { backgroundImage: `url("${detail.url}")` } : undefined}>
              {!detail.url && <span className="bk-thumb-ph">{detail.fileName || detail.tag}</span>}
            </div>
            <div className="bk-modal-body">
              <div className="bk-modal-fn">{detail.fileName || t('brandkit.untitled')}</div>
              {/* TASK-325 fix (major #3): a post-origin asset (tag='post') is a
                  reference into the post's own image, server-set-only — the
                  server now 400s a retag/set-as-logo attempt on it
                  (POST_ASSET_NOT_RETAGGABLE), so the picker that would only
                  invite that rejection is hidden here instead. */}
              {detail.tag !== 'post' && (
                <>
                  <div className="bk-modal-sec">{t('brandkit.tagLabel')}</div>
                  <div className="bk-tagrow">
                    {TAGS.map((tag) => (
                      <button key={tag} type="button" className={`bk-tagpick ${detail.tag === tag ? 'is-on' : ''}`} disabled={!brandingAllowed || busyId === detail.id} onClick={() => retag(detail, tag)}>
                        <TagIcon tag={tag} />{t(`brandkit.tag_${tag}`)}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {detail.referencesDropped && (
                <div className="bk-modal-refnote">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3l18 18" /><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /></svg>
                  {t('brandkit.refsDroppedHint')}
                </div>
              )}

              {/* Font reference lives HERE too, not only in the grid kebab: the
                  detail sheet is where users look for per-asset actions. */}
              {typographyOn && (
                <button
                  type="button"
                  className={`bk-setlogo ${markedAssetSet.has(detail.id) ? 'is-current' : ''}`}
                  disabled={!brandingAllowed}
                  onClick={() => (markedAssetSet.has(detail.id)
                    ? removeTypographyReference(detail)
                    : setTypographyDefault(detail))}
                >
                  <span aria-hidden="true" className="bk-typo-aa">Aa</span>
                  {markedAssetSet.has(detail.id) ? t('typography.removeReference') : t('typography.markReference')}
                </button>
              )}

              {detail.tag === 'logo' && (
                <button
                  type="button"
                  className={`bk-setlogo ${detail.id === primaryLogoId ? 'is-current' : ''}`}
                  disabled={!brandingAllowed || busyId === detail.id || detail.id === primaryLogoId}
                  onClick={() => makePrimary(detail)}
                >
                  {brandingLocked ? <I.lock size={13} /> : <Star size={13} filled={detail.id === primaryLogoId} />}
                  {detail.id === primaryLogoId ? t('brandkit.isPrimary') : t('brandkit.makePrimary')}
                </button>
              )}

              <button type="button" className="bk-modal-del" disabled={busyId === detail.id} onClick={() => remove(detail)}>
                <I.trash size={13} /> {t('brandkit.delete')}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="bk-modal-bg bk-confirm-bg" onClick={() => setConfirmDel(null)}>
          <div className="bk-confirm" role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <span className="bk-confirm-ico"><I.trash size={20} /></span>
            {/* Delete-confirm copy lives under postPreview.* (same asset-delete
                semantics, translated x10) — brandkit.* never had these keys.
                TASK-325: a post-origin asset (tag='post') is a REFERENCE into
                the post's own media, not a private copy — removing it from
                Media never touches the post, so it gets its own, less
                alarming copy instead of the generic "can't be undone" wording. */}
            {confirmDel.tag === 'post' ? (
              <>
                <h3 className="bk-confirm-t">{t('brandkit.removeFromMediaTitle')}</h3>
                <p className="bk-confirm-b">{t('brandkit.removeFromMediaBody')}</p>
              </>
            ) : (
              <>
                <h3 className="bk-confirm-t">{t('postPreview.deleteTitle')}</h3>
                <p className="bk-confirm-b">{t('postPreview.deleteBody')}</p>
              </>
            )}
            {(() => {
              // Video assets have no still image at `url` (that's the mp4/mov
              // itself) — use the poster frame instead, same as the grid tile.
              const isVid = (confirmDel.mimeType || '').startsWith('video/');
              const thumb = isVid ? confirmDel.posterUrl : (confirmDel.url || confirmDel.thumbUrl);
              return thumb ? <div className="bk-confirm-thumb" style={{ backgroundImage: `url(${thumb})` }} /> : null;
            })()}
            <div className="bk-confirm-acts">
              <button type="button" className="btn btn-ghost" onClick={() => setConfirmDel(null)}>{t('common.cancel')}</button>
              <button type="button" className="btn bk-confirm-del" onClick={confirmRemove}>
                <I.trash size={14} /> {confirmDel.tag === 'post' ? t('brandkit.removeFromMedia') : t('brandkit.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      <LibraryPicker
        open={refPickerOpen}
        mode="guide"
        initial={pickedRefs}
        max={isPhotosMode ? MAX_REEL_FRAMES : MAX_GEN_REFS}
        confirmLabel={t('brandkit.addReferences')}
        onClose={() => setRefPickerOpen(false)}
        onConfirm={(chosen) => { setPickedRefs(chosen); setRefPickerOpen(false); }}
      />

      {/* Describe-mode reference-photo picker — a SEPARATE LibraryPicker instance
          (own open/initial/onConfirm) so it never collides with the image-mode /
          photos-mode picker above, which is mid-flight-bound to pickedRefs. */}
      <LibraryPicker
        open={describeRefPickerOpen}
        mode="guide"
        initial={describeRefs}
        max={MAX_GEN_REFS}
        confirmLabel={t('brandkit.addReferences')}
        onClose={() => setDescribeRefPickerOpen(false)}
        onConfirm={(chosen) => { setDescribeRefs(chosen); setDescribeRefPickerOpen(false); }}
      />

      {typographyOn && brandingAllowed && (
        <>
          <FontCatalogModal
            open={fontCatalogOpen}
            brandName={brandName}
            uiLanguage={i18n.resolvedLanguage || i18n.language}
            needsCyrillic={languagesNeedCyrillic(typographyLanguages)}
            rendering={fontRendering}
            error={fontError}
            onClose={() => { if (!fontRendering) setFontCatalogOpen(false); }}
            onConfirm={confirmCatalogFont}
          />
          <LibraryPicker
            open={fontMediaPickerOpen}
            mode="guide"
            initial={typographyDefaultAsset ? [typographyDefaultAsset] : []}
            max={1}
            assetIds={markedAssetIds}
            title={t('typography.chooseMediaTitle')}
            confirmLabel={t('typography.useReference')}
            onClose={() => setFontMediaPickerOpen(false)}
            onConfirm={(chosen) => {
              if (chosen[0]) setTypographyDefault(chosen[0]);
              setFontMediaPickerOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
}
